import React, { useState, useEffect } from 'react';
import {
    Card, Button, Table, Modal, Select, DatePicker, message, Spin,
    Space, Tag, Row, Col, Statistic, Popconfirm, Radio, Input,
    Divider, Progress, Badge
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, EyeOutlined,
    UserOutlined, TeamOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import util from '../../utils/util';
import { usePeriods } from '../../hooks/usePeriods';
import FacilityService from '../../services/FacilityService';
import { AntdSelect, AntdDatepicker } from '../../utils/Antd';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { trailCreate, trailDelete, trailPageView, TRAIL_MODULES } from '../../utils/activityTrail';
const { TextArea } = Input;

// ─── Score map ───────────────────────────────────────────────────────────────
const SCORE_MAP = {
    'Very Satisfied':100,'Very Good':100,'Very good':100,
    'Satisfied':75,'Good':75,'good':75,
    'Average':50,
    'Unsatisfied':25,'Poor':25,'poor':25,
    'Very Unsatisfied':0,'Very Poor':0,'Very poor':0,
    'Yes':100,'No':0,
    '< 30 Minutes':100,'30min-1 Hour':75,'1 Hour-2 Hours':25,'More than 2 Hours':0,
};

const scoreColor = (s) => {
    if (s>=75) return '#52c41a';
    if (s>=50) return '#faad14';
    if (s>=25) return '#fa8c16';
    return '#f5222d';
};

// Quarters are computed RELATIVE to the selected period's own period_start —
// not calendar-year quarters. So if a period starts 01 Apr, Q1 = Apr-Jun,
// Q2 = Jul-Sep, Q3 = Oct-Dec, Q4 = Jan-Mar, matching whatever fiscal
// convention that period actually uses.
const getQuarterRanges = (period) => {
    if (!period?.period_start) return {};
    const start = moment(period.period_start).startOf('day');
    const ranges = {};
    ['Q1','Q2','Q3','Q4'].forEach((q, i) => {
        const qStart = start.clone().add(i*3, 'months');
        const qEnd   = start.clone().add((i+1)*3, 'months').subtract(1, 'day');
        ranges[q] = [qStart, qEnd];
    });
    return ranges;
};

const SECTION_COLORS = {
    'TIMELINESS':'#0ea5e9',
    'ATTITUDE':'#8b5cf6',
    'FACILITY':'#10b981',
    'SUPPLIES':'#f59e0b',
    'PATIENT RIGHTS':'#ef4444',
    'OVERALL':'#6366f1',
};

// ─── Inpatient Questions ─────────────────────────────────────────────────────
const IP_QUESTIONS = [
    { ref:'IP.1', section:'TIMELINESS',
      text:'After admission how long did you wait before being allocated a ward and bed?',
      type:'radio', options:['< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'IP.2', section:'ATTITUDE',
      text:'During your stay, how was your interaction with health workers (cadres)?',
      type:'matrix', sub_items:['Nurse','Doctor','Caterers','Laboratory','Pharmacy'],
      options:['Very good','Good','Poor','Very poor'] },
    { ref:'IP.3', section:'FACILITY',
      text:'How is signage for directions to service points? (Within and outside the facility)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.4', section:'FACILITY',
      text:'How is the facility cleanliness? (TOILETS, SHOWERS, LINEN, WARD)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.5', section:'SUPPLIES',
      text:'Did you receive all the prescribed medication during your consultation/visit?',
      type:'yesno' },
    { ref:'IP.6', section:'SUPPLIES',
      text:'Did you receive your meals on time?',
      type:'yesno' },
    { ref:'IP.7', section:'SUPPLIES',
      text:'How was the presentation of the food?',
      type:'radio', options:['Very good','Good','Average','Poor','Very Poor'] },
    { ref:'IP.8', section:'PATIENT RIGHTS',
      text:'Respect of privacy during your stay (visual/audio)',
      type:'yesno' },
    { ref:'IP.9', section:'PATIENT RIGHTS',
      text:'Did the health practitioner provide an explanation for your condition, procedures to care and treatment plan?',
      type:'yesno' },
    { ref:'IP.10', section:'PATIENT RIGHTS',
      text:'Were you aware that you could make a formal complaint in the hospital?',
      type:'yesno' },
    { ref:'IP.11', section:'OVERALL',
      text:'How satisfactory were the hospital routines and procedures for you?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

// ─── Outpatient Questions ────────────────────────────────────────────────────
const OP_QUESTIONS = [
    { ref:'OP.1', section:'TIMELINESS',
      text:'How long did you wait before receiving service from these service points/providers? (consider opening time of the facility 0800hrs)',
      type:'matrix', sub_items:['Screening','Nurse','Doctor','Laboratory','Medical Imaging (X-Ray, scan etc.)','Pharmacy'],
      options:['N/A','< 30 Minutes','30min-1 Hour','1 Hour-2 Hours','More than 2 Hours'] },
    { ref:'OP.2', section:'TIMELINESS',
      text:'How would you rate the time management during service delivery in the facility?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.3', section:'ATTITUDE',
      text:'How was the attitude of the health care provider?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.4', section:'FACILITY',
      text:'How is signage for directions to service points? (Within and outside the facility)',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.5', section:'FACILITY',
      text:'How is the facility cleanliness?',
      type:'radio', options:['Very Good','Good','Average','Poor','Very Poor'] },
    { ref:'OP.6', section:'SUPPLIES',
      text:'Did you receive all the prescribed medication during your consultation/visit?',
      type:'yesno' },
    { ref:'OP.7', section:'PATIENT RIGHTS',
      text:'Were you treated with respect?',
      type:'yesno' },
    { ref:'OP.8', section:'PATIENT RIGHTS',
      text:'Did the health practitioner provide an explanation for your condition, procedures to care and treatment plan?',
      type:'yesno' },
    { ref:'OP.9', section:'PATIENT RIGHTS',
      text:'Were you given clear guidance on follow up care?',
      type:'yesno' },
    { ref:'OP.10', section:'OVERALL',
      text:'How satisfied were you with the service you received?',
      type:'radio', options:['Very Satisfied','Satisfied','Unsatisfied','Very Unsatisfied'] },
];

// â”€â”€â”€ Patient-facing service categories (replaces free-text department entry) â”€
// Privacy-conscious groupings â€” sensitive service lines (HIV, mental health,
// TB, chronic conditions) are deliberately merged into one neutral bucket
// so a patient can indicate "ongoing treatment" without disclosing which
// condition brought them in.
const OP_SERVICE_CATEGORIES = [
    'General Outpatient Consultation',
    'Maternal & Child Health',
    'Chronic Disease / Ongoing Treatment',
    'Emergency / Casualty',
    'Laboratory / Diagnostic Tests',
    'Pharmacy',
    'Family Planning',
    'Other',
];
const IP_WARD_CATEGORIES = [
    'Maternity Ward',
    'Surgical Ward',
    'Medical Ward - Chronic Disease / Ongoing Treatment',
    'Paediatric Ward',
    'Emergency / Trauma Admission',
    'Other Ward / Admission',
];

// ─── Medication groups ───────────────────────────────────────────────────────
const MEDICATION_GROUPS = {
    'NCD': [
        'Antihypertensives (Blood pressure)','Antidiabetics (Diabetes)','Statins (Cholesterol)',
        'Aspirin / Antiplatelet','Inhaler / Asthma medication','Anticoagulants','Other NCD medication',
    ],
    'HIV': [
        'ARVs — 1st line (TLD, TLE)','ARVs — 2nd line','ARVs — 3rd line',
        'OI Prophylaxis (Cotrimoxazole)','TB Preventive Therapy (TPT/INH)','Other HIV-related medication',
    ],
    'TB': [
        'TB Treatment — Intensive Phase (2RHZE)','TB Treatment — Continuation Phase (4RH)',
        'MDR-TB Regimen','TB Prophylaxis (INH)','Other TB medication',
    ],
    'General': [
        'Analgesics (Pain relief)','Antibiotics','Antifungals','Antiparasitics (Deworming, Antimalarials)',
        'Vitamins / Supplements','Contraceptives','Wound care / Topical','Oral Rehydration Salts (ORS)','Other general medication',
    ],
};
const MED_GROUP_COLORS = { 'NCD':'#ef4444', 'HIV':'#8b5cf6', 'TB':'#f59e0b', 'General':'#10b981' };

// ─── Helper: parse services_offered (handles array or JSON string) ────────────
function parseServices(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
}

// ─── Shared UI atoms ─────────────────────────────────────────────────────────
const SectionHdr = ({ title }) => (
    <div style={{
        backgroundColor: SECTION_COLORS[title]||'#1890ff', color:'#fff', fontWeight:700, fontSize:12,
        padding:'7px 12px', borderRadius:5, marginTop:18, marginBottom:10, letterSpacing:'0.4px',
    }}>{title}</div>
);

const OptionBtn = ({ label, selected, onClick, color }) => {
    const isGood = ['Very Good','Very good','Very Satisfied','Yes','< 30 Minutes'].includes(label);
    const isBad  = ['Very Poor','Very poor','Very Unsatisfied','No','More than 2 Hours'].includes(label);
    const bg = selected ? (color || (isGood ? '#52c41a' : isBad ? '#f5222d' : '#1890ff')) : '#f0f0f0';
    return (
        <button onClick={onClick} style={{
            padding:'5px 13px', borderRadius:5,
            border: selected ? 'none' : '1px solid #d9d9d9',
            backgroundColor:bg, color: selected ? '#fff' : '#555',
            cursor:'pointer', fontSize:12, fontWeight: selected ? 700 : 400,
            transition:'all 0.18s', margin:'2px',
        }}>{label}</button>
    );
};

// Full-width, stacked toggle button for multi-select lists (service category,
// ward selection) — replaces the multi-select dropdown for easier selection.
// Sized to match this file's denser desktop-form rows rather than the
// larger touch-friendly version used on the public patient form.
const MultiSelectBtn = ({ label, selected, onClick }) => (
    <button type="button" onClick={onClick} style={{
        display:'flex', alignItems:'center', gap:8, width:'100%', textAlign:'left',
        padding:'6px 10px', borderRadius:6, marginBottom:5,
        border: selected ? 'none' : '1px solid #d9d9d9',
        background: selected ? '#1890ff' : '#f0f0f0',
        color: selected ? '#fff' : '#555',
        fontSize:12, fontWeight: selected ? 700 : 400,
        cursor:'pointer', transition:'all 0.18s',
    }}>
        <span style={{
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            width:15, height:15, borderRadius:3, flexShrink:0,
            border: selected ? 'none' : '1.5px solid #bbb',
            background: selected ? 'rgba(255,255,255,0.28)' : 'transparent',
            fontSize:10, lineHeight:1,
        }}>{selected ? '✓' : ''}</span>
        {label}
    </button>
);

const CircleSelect = ({ selected, onClick, color }) => (
    <div onClick={onClick} style={{
        width:22, height:22, borderRadius:'50%',
        border:`2px solid ${selected ? (color||'#1890ff') : '#d9d9d9'}`,
        backgroundColor: selected ? (color||'#1890ff') : 'transparent',
        margin:'0 auto', cursor:'pointer',
        display:'flex', alignItems:'center', justifyContent:'center',
    }}>
        {selected && <div style={{width:10,height:10,borderRadius:'50%',backgroundColor:'#fff'}}/>}
    </div>
);

// ─── Exportable chart wrapper ─────────────────────────────────────────────────
// Every chart on this page (analytics dashboard + narrative report) goes
// through this so "the graphs should be exportable" is satisfied uniformly
// in one place, rather than bolting an export button onto each chart
// individually. Uses ECharts' own getDataURL() rather than html2canvas —
// simpler and more reliable for a single chart instance than capturing a
// whole DOM node.
function ExportableChart({ option, height=280, filename='chart', title }) {
    const chartRef = React.useRef(null);
    const exportPNG = () => {
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) { message.error('Chart not ready yet'); return; }
        const url = instance.getDataURL({ type:'png', pixelRatio:2, backgroundColor:'#fff' });
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename.replace(/[^a-zA-Z0-9_-]/g,'_')}.png`;
        a.click();
    };
    // The title has to live INSIDE the ECharts option, not as a separate
    // HTML element next to the chart — getDataURL() only ever captures the
    // chart's own canvas, so a title rendered outside it looked identical
    // on screen but was silently missing from every exported PNG. Baking it
    // into option.title makes the on-screen render and the exported image
    // the exact same thing.
    const mergedOption = title ? {
        ...option,
        title: { text: title, left: 'center', top: 6, textStyle: { fontSize: 13, fontWeight: 700, color: '#333' } },
        grid: option.grid ? { ...option.grid, top: (typeof option.grid.top === 'number' ? option.grid.top : 10) + 26 } : option.grid,
    } : option;
    const actualHeight = title ? height + 28 : height;
    return (
        <div className="chart-box" style={{ position:'relative' }}>
            <Button size="small" icon={<span>⬇</span>} onClick={exportPNG}
                style={{ position:'absolute', top:0, right:0, zIndex:2 }}>PNG</Button>
            <ReactECharts ref={chartRef} option={mergedOption} style={{ height:actualHeight }} notMerge lazyUpdate/>
        </div>
    );
}

// ─── SURVEY FORM ──────────────────────────────────────────────────────────────
function SurveyForm({ type, facilities, onSaved, onClose, periods, currentPeriod, staffFacilityId = null }) {
    const questions = type === 'inpatient' ? IP_QUESTIONS : OP_QUESTIONS;
    // Pre-lock to staff facility if assigned
    const lockedFac = staffFacilityId
        ? facilities.find(f => f.id === staffFacilityId)
        : null;

    const [hdr, setHdr] = useState({
        survey_date:   moment(),
        period_label:  '',
        patient_gender:'', patient_age:'', paid_for_service:'', amount_paid:'', hometown:'',
        fac_services:  [],
        facility_id:   staffFacilityId || undefined,
    });

    useEffect(() => {
        if (currentPeriod) setHdr(h => ({ ...h, period_label: currentPeriod.label }));
    }, [currentPeriod]);

    const [resp, setRespState] = useState({});
    const [comments, setComments] = useState('');
    const [loading, setLoading] = useState(false);
    const [prescriptionFile, setPrescriptionFile] = useState(null);
    const [prescriptionPreview, setPrescriptionPreview] = useState(null);
    const isIP = type === 'inpatient';

    const setR = (ref, val, sub=null) => {
        const k = sub ? `${ref}__${sub}` : ref;
        setRespState(p => ({...p, [k]: val}));
    };
    const getR = (ref, sub=null) => resp[sub ? `${ref}__${sub}` : ref];

    const pct = () => {
        let tot=0, done=0;
        questions.forEach(q => {
            if (q.type==='matrix') q.sub_items.forEach(s => { tot++; if (getR(q.ref,s)) done++; });
            else { tot++; if (getR(q.ref)) done++; }
        });
        return tot ? Math.round((done/tot)*100) : 0;
    };

    const payload = () => {
        const arr=[];
        questions.forEach(q => {
            if (q.type==='matrix') {
                q.sub_items.forEach(s => { const v=getR(q.ref,s); if(v) arr.push({question_ref:q.ref,sub_item:s,response_value:v}); });
            } else {
                const v=getR(q.ref);
                if(v) arr.push({question_ref:q.ref,sub_item:null,response_value:v});
                if (v === 'No' && (q.text.toLowerCase().includes('medication') || q.text.toLowerCase().includes('prescribed'))) {
                    Object.keys(MEDICATION_GROUPS).forEach(grp => {
                        const key = `${q.ref}__notreceived__${grp}`;
                        const meds = getR(key);
                        if (Array.isArray(meds) && meds.length > 0) {
                            arr.push({ question_ref: key, sub_item: grp, response_value: meds.join('|') });
                        }
                    });
                }
            }
        });
        return arr;
    };

    const save = async () => {
        if (!hdr.facility_id) { message.error('Please select a facility'); return; }
        const fac = facilities.find(f=>f.id===hdr.facility_id);
        setLoading(true);
        try {
            const basePayload = {
                survey_type: type,
                period_label: hdr.period_label || currentPeriod?.label || '',
                survey_date: moment(hdr.survey_date).format('YYYY-MM-DD'),
                facility_id: hdr.facility_id,
                facility_name: fac?.name||'',
                region: hdr.region_manual || fac?.region || '',
                ward_admitted: JSON.stringify(hdr.ward_admitted || []),
                departments_visited: JSON.stringify(hdr.departments_visited || []),
                arrival_time: hdr.arrival_time || '',
                departure_time: hdr.departure_time || '',
                attitude_comments: hdr.attitude_comments || '',
                supplies_comments: hdr.supplies_comments || '',
                patient_gender:   hdr.patient_gender   || '',
                patient_age:      hdr.patient_age       || '',
                paid_for_service: hdr.paid_for_service  || '',
                amount_paid:      hdr.paid_for_service === 'Yes' ? (hdr.amount_paid || '') : '',
                hometown:         hdr.hometown || '',
                comments,
            };

            if (prescriptionFile) {
                // A file is attached — submit as multipart/form-data so the image reaches the server.
                const fd = new FormData();
                Object.entries(basePayload).forEach(([k, v]) => {
                    if (v !== null && v !== undefined) fd.append(k, v);
                });
                fd.append('responses', JSON.stringify(payload()));
                fd.append('prescription_image', prescriptionFile);
                await QoCService.saveSurvey(fd);
            } else {
                // No file — keep the existing plain-object submission path unchanged.
                await QoCService.saveSurvey({ ...basePayload, responses: payload() });
            }
            const facName = facilities.find(f => f.id === hdr.facility_id)?.name || String(hdr.facility_id);
            trailCreate(TRAIL_MODULES.SURVEY, type === 'inpatient' ? 'Inpatient Survey' : 'Outpatient Survey', '',
                { facilityName: facName, period: hdr.period_label });
            message.success('Survey saved');
            onSaved();
        } catch { message.error('Failed to save survey'); }
        finally { setLoading(false); }
    };

    const completion = pct();
    const accentColor = isIP ? '#003580' : '#006837';
    const sections = [...new Set(questions.map(q=>q.section))];

    const renderQuestion = (q) => {
        const sColor = SECTION_COLORS[q.section]||'#1890ff';
        const qNum = q.ref.split('.')[1];

        if (q.type === 'yesno') {
            const isMedQuestion = q.text.toLowerCase().includes('medication') || q.text.toLowerCase().includes('prescribed');
            const answeredNo = getR(q.ref) === 'No';
            return (
                <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>
                        <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                    </div>
                    <div style={{display:'flex',gap:10}}>
                        {['Yes','No'].map(opt => <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt} onClick={()=>setR(q.ref,opt)} />)}
                    </div>
                    {isMedQuestion && answeredNo && (
                        <div style={{ marginTop:12, padding:'10px 12px', background:'#fff7ed', borderRadius:8, border:'1px solid #fed7aa' }}>
                            <div style={{ fontWeight:700, fontSize:11, color:'#c2410c', marginBottom:10 }}>
                                ⚠ Which medication(s) were not received? (select all that apply)
                            </div>
                            {Object.entries(MEDICATION_GROUPS).map(([group, meds]) => {
                                const groupKey = `${q.ref}__notreceived__${group}`;
                                const selected = getR(groupKey) || [];
                                const groupColor = MED_GROUP_COLORS[group] || '#888';
                                return (
                                    <div key={group} style={{ marginBottom:10 }}>
                                        <div style={{ fontWeight:700, fontSize:11, color: groupColor, marginBottom:5, display:'flex', alignItems:'center', gap:6 }}>
                                            <span style={{ width:8, height:8, borderRadius:'50%', background:groupColor, display:'inline-block' }}/>
                                            {group}
                                        </div>
                                        <div style={{ display:'flex', flexWrap:'wrap', gap:4, paddingLeft:14 }}>
                                            {meds.map(med => {
                                                const isChecked = Array.isArray(selected) && selected.includes(med);
                                                return (
                                                    <div key={med}
                                                        onClick={() => {
                                                            const cur = Array.isArray(getR(groupKey)) ? getR(groupKey) : [];
                                                            const next = isChecked ? cur.filter(m => m !== med) : [...cur, med];
                                                            setR(groupKey, next);
                                                        }}
                                                        style={{
                                                            padding:'3px 10px', borderRadius:12, fontSize:11, cursor:'pointer', userSelect:'none',
                                                            background: isChecked ? groupColor : '#fff',
                                                            color: isChecked ? '#fff' : '#555',
                                                            border: `1px solid ${isChecked ? groupColor : '#e0e0e0'}`,
                                                            fontWeight: isChecked ? 700 : 400, transition: 'all 0.12s',
                                                        }}>
                                                        {isChecked ? '✓ ' : ''}{med}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            );
        }

        if (q.type === 'matrix') return (
            <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:10}}>
                    <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                </div>
                <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:400}}>
                        <thead>
                            <tr style={{backgroundColor:'#f0f0f0'}}>
                                <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid #e0e0e0',minWidth:160}}></th>
                                {q.options.map(opt => (
                                    <th key={opt} style={{padding:'6px 8px',textAlign:'center',borderBottom:'1px solid #e0e0e0',color:sColor,fontSize:11}}>{opt}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {q.sub_items.map((sub,ri) => (
                                <tr key={sub} style={{backgroundColor:ri%2?'#fafafa':'#fff'}}>
                                    <td style={{padding:'7px 10px',fontWeight:600,borderBottom:'1px solid #f5f5f5'}}>{sub}</td>
                                    {q.options.map(opt => (
                                        <td key={opt} style={{textAlign:'center',borderBottom:'1px solid #f5f5f5',padding:'5px'}}>
                                            <CircleSelect selected={getR(q.ref,sub)===opt} onClick={()=>setR(q.ref,opt,sub)} color={sColor} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        );

        return (
            <Card key={q.ref} size="small" style={{ marginBottom:8, borderLeft:`3px solid ${sColor}` }}>
                <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>
                    <Tag color={sColor} style={{border:'none',color:'#fff',backgroundColor:sColor}}>Q{qNum}</Tag> {q.text}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                    {q.options.map(opt => <OptionBtn key={opt} label={opt} selected={getR(q.ref)===opt} onClick={()=>setR(q.ref,opt)} color={sColor} />)}
                </div>
            </Card>
        );
    };

    return (
        <div style={{fontFamily:'Arial,sans-serif'}}>
            {/* Banner */}
            <div style={{background:`linear-gradient(135deg,${accentColor},${isIP?'#0050b3':'#00a651'})`,borderRadius:8,padding:'12px 18px',marginBottom:14,color:'#fff'}}>
                <div style={{fontWeight:800,fontSize:14}}>CUSTOMER SATISFACTION SURVEY — {isIP?'INPATIENT':'OUTPATIENT'}</div>
                <div style={{fontSize:11,opacity:0.85,marginTop:3}}>Ministry of Health · National Quality Management Program · Please TICK your answer to each question.</div>
            </div>

            {/* Progress */}
            <div style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                    <span style={{fontSize:11,color:'#888'}}>Completion</span>
                    <span style={{fontSize:11,fontWeight:700,color:scoreColor(completion)}}>{completion}%</span>
                </div>
                <Progress percent={completion} showInfo={false} strokeColor={scoreColor(completion)} size="small" />
            </div>

            {/* Header fields */}
            <Card size="small" style={{marginBottom:12,backgroundColor:'#fafafa'}}>
                <Row gutter={12}>
                    <Col span={12}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>NAME OF FACILITY *</label>
                        <AntdSelect showSearch options={facilities} value={hdr.facility_id}
                            onChange={v => {
                                const fac = facilities.find(f => f.id === v);
                                // Parse services_offered so survey type awareness works
                                const svcs = parseServices(fac?.services_offered);
                                setHdr(h => ({
                                    ...h,
                                    facility_id:   v,
                                    region_manual: fac?.region || fac?.located_region || h.region_manual || '',
                                    fac_services:  svcs,
                                }));
                            }}
                            style={{width:'100%'}} />
                    </Col>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>DATE</label>
                        <AntdDatepicker value={hdr.survey_date} onChange={v=>setHdr({...hdr,survey_date:v})} style={{width:'100%'}} />
                    </Col>
                    <Col span={8}>
                        <label style={{fontWeight:600,display:'block',marginBottom:4}}>Assessment Period</label>
                        <Select style={{width:'100%'}}
                            value={hdr.period_label || undefined}
                            placeholder="Select period"
                            onChange={v=>setHdr({...hdr,period_label:v})}>
                            {(periods||[]).map(p=>(
                                <Select.Option key={p.label} value={p.label}>
                                    {p.is_current==1?'★ ':''}{p.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={12}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>{isIP?'WARD / REASON FOR ADMISSION':'SERVICE CATEGORY (select all that apply)'}</label>
                        <div>
                            {(isIP?IP_WARD_CATEGORIES:OP_SERVICE_CATEGORIES).map(c=>{
                                const field = isIP ? 'ward_admitted' : 'departments_visited';
                                const list = Array.isArray(hdr[field]) ? hdr[field] : [];
                                const selected = list.includes(c);
                                return (
                                    <MultiSelectBtn key={c} label={c} selected={selected}
                                        onClick={()=>{
                                            const next = selected ? list.filter(v=>v!==c) : [...list, c];
                                            setHdr({...hdr, [field]: next});
                                        }}/>
                                );
                            })}
                        </div>
                    </Col>
                    <Col span={6} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>ARRIVAL TIME</label>
                        <Input type="time" value={hdr.arrival_time} onChange={e=>setHdr({...hdr,arrival_time:e.target.value})} />
                    </Col>
                    <Col span={6} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>DEPARTURE TIME</label>
                        <Input type="time" value={hdr.departure_time} onChange={e=>setHdr({...hdr,departure_time:e.target.value})} />
                    </Col>
                    <Col span={12} style={{marginTop:8}}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>REGION</label>
                        <Input placeholder="Auto-filled from facility" value={hdr.region_manual} onChange={e=>setHdr({...hdr,region_manual:e.target.value})} />
                    </Col>
                </Row>
            </Card>

            {/* Patient demographics */}
            <Card size="small" style={{marginBottom:12,backgroundColor:'#fafafa'}}>
                <Row gutter={12}>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>PATIENT GENDER</label>
                        <Radio.Group value={hdr.patient_gender} onChange={e=>setHdr({...hdr,patient_gender:e.target.value})} size="small">
                            <Radio.Button value="Male">Male</Radio.Button>
                            <Radio.Button value="Female">Female</Radio.Button>
                            <Radio.Button value="Other">Other</Radio.Button>
                        </Radio.Group>
                    </Col>
                    <Col span={4}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>AGE (years)</label>
                        <Input type="number" min="0" max="120" placeholder="e.g. 34"
                            value={hdr.patient_age}
                            onChange={e=>setHdr({...hdr,patient_age:e.target.value})}
                            style={{width:'100%'}}/>
                    </Col>
                    <Col span={8}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>HOMETOWN / COMMUNITY</label>
                        <Input placeholder="e.g. Manzini, Nhlangano, Mbabane"
                            value={hdr.hometown}
                            onChange={e=>setHdr({...hdr,hometown:e.target.value})}
                            style={{width:'100%'}}/>
                    </Col>
                    <Col span={6}>
                        <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>PAID FOR SERVICES?</label>
                        <Radio.Group value={hdr.paid_for_service} onChange={e=>setHdr({...hdr,paid_for_service:e.target.value})} size="small">
                            <Radio.Button value="Yes">Yes</Radio.Button>
                            <Radio.Button value="No">No</Radio.Button>
                        </Radio.Group>
                    </Col>
                    {hdr.paid_for_service === 'Yes' && (
                        <Col span={6}>
                            <label style={{fontSize:11,fontWeight:700,display:'block',marginBottom:3}}>AMOUNT PAID (E)</label>
                            <Input type="number" min="0" placeholder="e.g. 50"
                                value={hdr.amount_paid}
                                onChange={e=>setHdr({...hdr,amount_paid:e.target.value})}
                                style={{width:'100%'}}
                                prefix="E"/>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* Questions by section */}
            {sections.map(section => {
                const qs = questions.filter(q=>q.section===section);
                return (
                    <div key={section}>
                        <SectionHdr title={section} />
                        {qs.map(q => renderQuestion(q))}
                        {section==='ATTITUDE' && (
                            <Card size="small" style={{marginBottom:8,backgroundColor:'#fefefe'}}>
                                <label style={{fontSize:11,fontWeight:700,color:'#888'}}>COMMENTS:</label>
                                <Input style={{marginTop:4}} placeholder="Optional comments..." value={hdr.attitude_comments}
                                    onChange={e=>setHdr({...hdr,attitude_comments:e.target.value})} />
                            </Card>
                        )}
                        {section==='SUPPLIES' && (
                            <Card size="small" style={{marginBottom:8,backgroundColor:'#fefefe'}}>
                                <div style={{marginBottom:12}}>
                                    <label style={{fontSize:11,fontWeight:700,color:'#888',display:'block',marginBottom:6}}>
                                        PRESCRIPTION (optional)
                                    </label>
                                    <div style={{ border:`2px dashed ${prescriptionFile ? '#52c41a' : '#d9d9d9'}`,
                                        borderRadius:8, padding:'10px 14px', textAlign:'center',
                                        background: prescriptionFile ? '#f6ffed' : '#fafafa', cursor:'pointer' }}
                                        onClick={() => document.getElementById(`rx_upload_${type}`).click()}>
                                        {prescriptionPreview
                                            ? <img src={prescriptionPreview} alt="prescription" style={{ maxWidth:'100%', maxHeight:160, borderRadius:6 }}/>
                                            : <div style={{ color:'#888', fontSize:12 }}>Click to attach a photo of the prescription<br/><span style={{ fontSize:11 }}>JPG, PNG, WEBP accepted</span></div>
                                        }
                                    </div>
                                    <input id={`rx_upload_${type}`} type="file" accept="image/*" capture="environment"
                                        style={{ display:'none' }}
                                        onChange={e => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            const reader = new FileReader();
                                            reader.onload = ev => {
                                                const img = new window.Image();
                                                img.onload = () => {
                                                    const canvas = document.createElement('canvas');
                                                    const MAX = 800;
                                                    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
                                                    canvas.width  = Math.round(img.width  * scale);
                                                    canvas.height = Math.round(img.height * scale);
                                                    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                                                    canvas.toBlob(blob => {
                                                        const compressed = new File([blob], file.name, { type:'image/jpeg' });
                                                        setPrescriptionFile(compressed);
                                                        setPrescriptionPreview(canvas.toDataURL('image/jpeg', 0.8));
                                                    }, 'image/jpeg', 0.8);
                                                };
                                                img.src = ev.target.result;
                                            };
                                            reader.readAsDataURL(file);
                                        }}/>
                                    {prescriptionFile && (
                                        <button onClick={() => { setPrescriptionFile(null); setPrescriptionPreview(null); }}
                                            style={{ marginTop:6, background:'none', border:'none', color:'#f5222d', cursor:'pointer', fontSize:11 }}>
                                            Remove
                                        </button>
                                    )}
                                </div>
                                <label style={{fontSize:11,fontWeight:700,color:'#888'}}>COMMENTS:</label>
                                <Input style={{marginTop:4}} placeholder="Optional comments..." value={hdr.supplies_comments}
                                    onChange={e=>setHdr({...hdr,supplies_comments:e.target.value})} />
                            </Card>
                        )}
                    </div>
                );
            })}

            {/* Final comments */}
            <Card size="small" style={{backgroundColor:'#f9f9f9',marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:11,marginBottom:5}}>
                    THANK YOU FOR YOUR PARTICIPATION. IF YOU HAVE ANY SUGGESTIONS OR COMMENTS PLEASE WRITE THEM BELOW:
                </div>
                <TextArea rows={3} value={comments} onChange={e=>setComments(e.target.value)} placeholder="Your suggestions or comments..." />
            </Card>

            <div style={{display:'flex',justifyContent:'flex-end',gap:10,paddingTop:6}}>
                <Button onClick={onClose}>Cancel</Button>
                <Button type="primary" loading={loading} onClick={save} disabled={!hdr.facility_id}
                    style={{backgroundColor:accentColor}}>
                    Submit Survey
                </Button>
            </div>
        </div>
    );
}

// ─── SURVEY DETAIL ────────────────────────────────────────────────────────────
function SurveyDetail({ survey }) {
    if (!survey) return null;
    const questions = survey.survey_type==='inpatient' ? IP_QUESTIONS : OP_QUESTIONS;
    const rm = {};
    (survey.responses||[]).forEach(r => {
        rm[r.sub_item ? `${r.question_ref}__${r.sub_item}` : r.question_ref] = { v:r.response_value, s:parseFloat(r.numeric_score||0) };
    });
    const sections = [...new Set(questions.map(q=>q.section))];

    return (
        <div>
            <Row gutter={16} style={{marginBottom:12}}>
                <Col span={9}><strong>Facility:</strong> {survey.facility_name}</Col>
                <Col span={7}><strong>Date:</strong> {moment(survey.survey_date).format('DD MMM YYYY')}</Col>
                <Col span={8}>
                    <strong>Score: </strong>
                    <span style={{fontWeight:800,fontSize:17,color:scoreColor(parseFloat(survey.overall_score||0))}}>
                        {parseFloat(survey.overall_score||0).toFixed(1)}%
                    </span>
                </Col>
                {survey.patient_gender && <Col span={6} style={{marginTop:6}}><strong>Gender:</strong> {survey.patient_gender}</Col>}
                {survey.patient_age    && <Col span={4} style={{marginTop:6}}><strong>Age:</strong> {survey.patient_age} yrs</Col>}
                {survey.paid_for_service && <Col span={8} style={{marginTop:6}}>
                    <strong>Payment:</strong> {survey.paid_for_service}
                    {survey.paid_for_service === 'Yes' && survey.amount_paid &&
                        <span style={{marginLeft:6,color:'#fa8c16',fontWeight:700}}>E{survey.amount_paid}</span>}
                </Col>}
                {survey.arrival_time && <Col span={6} style={{marginTop:6}}><strong>Arrival:</strong> {survey.arrival_time}</Col>}
                {survey.departure_time && <Col span={6} style={{marginTop:6}}><strong>Departure:</strong> {survey.departure_time}</Col>}
                {survey.departments_visited && <Col span={12} style={{marginTop:6}}><strong>Service Category:</strong> {
    (() => { try { const a = JSON.parse(survey.departments_visited); return Array.isArray(a) ? a.join(', ') : survey.departments_visited; } catch { return survey.departments_visited; } })()
}</Col>}
                {survey.ward_admitted && <Col span={12} style={{marginTop:6}}><strong>Ward / Admission Reason:</strong> {
    (() => { try { const a = JSON.parse(survey.ward_admitted); return Array.isArray(a) ? a.join(', ') : survey.ward_admitted; } catch { return survey.ward_admitted; } })()
}</Col>}
                {survey.region && <Col span={8} style={{marginTop:6}}><strong>Region:</strong> {survey.region}</Col>}
                {survey.hometown && <Col span={12} style={{marginTop:6}}><strong>Hometown:</strong> {survey.hometown}</Col>}
                {survey.distance_metres != null && <Col span={6} style={{marginTop:6}}>
                    <strong>Distance from facility:</strong> <span style={{color: parseInt(survey.distance_metres)<=100?'#52c41a':parseInt(survey.distance_metres)<=500?'#fa8c16':'#f5222d',fontWeight:700}}>
                        {parseInt(survey.distance_metres)>=1000
                            ? `${(parseInt(survey.distance_metres)/1000).toFixed(1)}km away`
                            : `${survey.distance_metres}m away`}
                    </span>
                </Col>}
                {survey.is_flagged==1 && <Col span={24} style={{marginTop:6}}>
                    <Tag color="red">Flagged: {survey.flag_reason}</Tag>
                </Col>}
                {survey.attitude_comments && <Col span={24} style={{marginTop:6}}><strong>Attitude Comments:</strong> {survey.attitude_comments}</Col>}
                {survey.supplies_comments && <Col span={24} style={{marginTop:6}}><strong>Supplies Comments:</strong> {survey.supplies_comments}</Col>}
            </Row>
            {sections.map(section => {
                const qs = questions.filter(q=>q.section===section);
                return (
                    <div key={section}>
                        <div style={{backgroundColor:SECTION_COLORS[section]||'#1890ff',color:'#fff',fontWeight:700,fontSize:11,padding:'4px 10px',borderRadius:4,margin:'12px 0 6px'}}>{section}</div>
                        {qs.map(q => {
                            if (q.type==='matrix') return (
                                <div key={q.ref} style={{marginBottom:8}}>
                                    <div style={{fontSize:11,color:'#555',marginBottom:4}}><Tag color="blue" size="small">{q.ref}</Tag> {q.text}</div>
                                    {q.sub_items.map(sub => {
                                        const r=rm[`${q.ref}__${sub}`];
                                        return r ? (
                                            <div key={sub} style={{display:'flex',alignItems:'center',gap:10,padding:'2px 0 2px 18px'}}>
                                                <span style={{width:180,fontSize:11,color:'#888'}}>{sub}</span>
                                                <Tag style={{fontSize:10}}>{r.v}</Tag>
                                                <span style={{fontSize:11,color:scoreColor(r.s),fontWeight:600}}>{r.s.toFixed(0)}%</span>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            );
                            const r=rm[q.ref];
                            const notRec = (survey.responses||[]).filter(x => x.question_ref && x.question_ref.startsWith(q.ref + '__notreceived__'));
                            return (
                                <div key={q.ref} style={{borderBottom:'1px solid #f5f5f5',padding:'4px 0'}}>
                                    <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                                        <Tag color="blue" size="small" style={{flexShrink:0,fontSize:10}}>{q.ref}</Tag>
                                        <span style={{fontSize:11,color:'#555',flex:1,lineHeight:1.5}}>{q.text}</span>
                                        {r
                                            ? <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                                                <Tag style={{fontSize:10}}>{r.v}</Tag>
                                                <span style={{fontSize:11,color:scoreColor(r.s),fontWeight:700}}>{r.s.toFixed(0)}%</span>
                                              </div>
                                            : <Tag color="default" style={{fontSize:10}}>—</Tag>
                                        }
                                    </div>
                                    {notRec.length > 0 && (
                                        <div style={{marginTop:4,marginLeft:20,padding:'6px 10px',background:'#fff7e6',borderRadius:6,border:'1px solid #ffd591'}}>
                                            <div style={{fontSize:10,fontWeight:700,color:'#d46b08',marginBottom:4}}>Medications not received:</div>
                                            {notRec.map(nr => (
                                                <div key={nr.question_ref} style={{fontSize:11,color:'#555'}}>
                                                    <strong>{nr.sub_item}:</strong> {nr.response_value}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
            {survey.comments && (
                <div style={{marginTop:12,padding:10,backgroundColor:'#fafafa',borderRadius:6,fontSize:11,color:'#555'}}>
                    <strong>Comments:</strong> {survey.comments}
                </div>
            )}
        </div>
    );
}


// ─── SURVEY ANALYTICS PANEL ──────────────────────────────────────────────────
function SurveyAnalyticsPanel({ filters, periods, facilities }) {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading]     = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [aFilters, setAFilters]   = useState({});
    const [medFilter, setMedFilter] = useState(null);

    useEffect(() => {
        if (!collapsed) loadAnalytics();
    }, [collapsed]);

    const loadAnalytics = async (f = aFilters) => {
        setLoading(true);
        try {
            const res = await QoCService.getSurveyAnalytics(f);
            setAnalytics(res.data?.data || null);
        } catch { message.error('Failed to load analytics'); }
        finally { setLoading(false); }
    };

    const SECTIONS   = ['TIMELINESS','ATTITUDE','FACILITY','SUPPLIES','PATIENT RIGHTS','OVERALL'];
    const SEC_COLORS = {'TIMELINESS':'#0ea5e9','ATTITUDE':'#8b5cf6','FACILITY':'#10b981','SUPPLIES':'#f59e0b','PATIENT RIGHTS':'#ef4444','OVERALL':'#6366f1'};
    const SAT_LABELS = {'very_satisfied':'Very Satisfied','satisfied':'Satisfied','unsatisfied':'Unsatisfied','very_unsatisfied':'Very Unsatisfied'};
    const SAT_COLORS = {'very_satisfied':'#52c41a','satisfied':'#a0d911','unsatisfied':'#fa8c16','very_unsatisfied':'#f5222d'};

    const facilityChart = () => {
        if (!analytics?.by_facility?.length) return null;
        const facs = [...new Set(analytics.by_facility.map(r => r.facility_name))];
        const ipData = facs.map(f => { const r = analytics.by_facility.find(x => x.facility_name===f && x.survey_type==='inpatient'); return r ? parseFloat(r.avg_score) : null; });
        const opData = facs.map(f => { const r = analytics.by_facility.find(x => x.facility_name===f && x.survey_type==='outpatient'); return r ? parseFloat(r.avg_score) : null; });
        return { facs, ipData, opData };
    };

    const sectionData = () => {
        const ip = analytics?.by_section?.inpatient  || {};
        const op = analytics?.by_section?.outpatient || {};
        return {
            ip: SECTIONS.map(s => ip[s] ?? 0),
            op: SECTIONS.map(s => op[s] ?? 0),
        };
    };

    const satData = (type) => {
        const rows = (analytics?.satisfaction || []).filter(r => r.survey_type === type);
        return Object.keys(SAT_LABELS).map(k => ({
            name: SAT_LABELS[k],
            value: parseInt(rows.find(r => r.overall_satisfaction === k)?.cnt || 0),
            color: SAT_COLORS[k],
        })).filter(x => x.value > 0);
    };

    const medData = () => {
        const meds = analytics?.medications || [];
        const medMap = {};
        meds.forEach(r => {
            // PHP already splits pipe-separated values; normalise dashes for display
            const med = r.medications.replace(/[\u2014\u2013\u2015\u00ad]/g, '-').trim();
            if (!med || med.toLowerCase() === 'other medication') return;
            if (!medMap[med]) medMap[med] = { ip:0, op:0, facilities:{} };
            if (r.survey_type === 'inpatient') medMap[med].ip += parseInt(r.cnt);
            else medMap[med].op += parseInt(r.cnt);
            medMap[med].facilities[r.facility_name] = (medMap[med].facilities[r.facility_name]||0) + parseInt(r.cnt);
        });
        return Object.entries(medMap).sort((a,b)=>(b[1].ip+b[1].op)-(a[1].ip+a[1].op)).slice(0,15);
    };

    const fc = facilityChart();
    const sd = sectionData();
    const md = medData();
    const categoryData = (type) => (analytics?.by_category?.[type] || []);

    return (
        <div style={{marginBottom:16}}>
            <div onClick={() => setCollapsed(!collapsed)} style={{
                background:'linear-gradient(135deg,#003580,#006837)', color:'#fff',
                padding:'12px 20px', borderRadius: collapsed ? 8 : '8px 8px 0 0',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
                userSelect:'none',
            }}>
                <div style={{fontWeight:700, fontSize:14}}>📊 Survey Analytics Dashboard</div>
                <div style={{fontSize:12, opacity:0.8}}>{collapsed ? '▼ Click to expand' : '▲ Click to collapse'}</div>
            </div>

            {!collapsed && (
                <div style={{border:'1px solid #e8e8e8', borderTop:'none', borderRadius:'0 0 8px 8px', padding:16, background:'#fafafa'}}>
                    <div style={{display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'flex-end'}}>
                        <div style={{minWidth:200}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>Facility</label>
                            <Select allowClear placeholder="All Facilities" style={{width:'100%'}} value={aFilters.facility_id}
                                onChange={v=>{const f={...aFilters,facility_id:v};setAFilters(f);loadAnalytics(f);}}>
                                {facilities.map(f=><Select.Option key={f.id} value={f.id}>{f.name}</Select.Option>)}
                            </Select>
                        </div>
                        <div style={{minWidth:180}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>Period</label>
                            <Select allowClear placeholder="All Periods" style={{width:'100%'}} value={aFilters.period_label}
                                onChange={v=>{const f={...aFilters,period_label:v,quarter:null};setAFilters(f);loadAnalytics(f);}}>
                                {periods.map(p=><Select.Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Select.Option>)}
                            </Select>
                        </div>
                        <div style={{minWidth:180}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>
                                Quarter <span style={{fontWeight:400, color:'#888'}}>(select a period first)</span>
                            </label>
                            <Select allowClear disabled={!aFilters.period_label}
                                placeholder={aFilters.period_label ? 'Whole period' : 'Select a period first'}
                                style={{width:'100%'}} value={aFilters.quarter}
                                onChange={v=>{
                                    const selPeriod = periods.find(p => p.label === aFilters.period_label);
                                    const range = v && selPeriod ? getQuarterRanges(selPeriod)[v] : null;
                                    const f = {
                                        ...aFilters, quarter:v||null,
                                        from_date: range ? range[0].format('YYYY-MM-DD') : undefined,
                                        to_date:   range ? range[1].format('YYYY-MM-DD') : undefined,
                                    };
                                    setAFilters(f); loadAnalytics(f);
                                }}>
                                {['Q1','Q2','Q3','Q4'].map(q => {
                                    const selPeriod = periods.find(p => p.label === aFilters.period_label);
                                    const range = selPeriod ? getQuarterRanges(selPeriod)[q] : null;
                                    return <Select.Option key={q} value={q}>{q}{range && <span style={{color:'#aaa',marginLeft:6,fontSize:11}}>({range[0].format('DD MMM')} – {range[1].format('DD MMM YYYY')})</span>}</Select.Option>;
                                })}
                            </Select>
                        </div>
                        <div style={{minWidth:160}}>
                            <label style={{fontWeight:600, display:'block', marginBottom:4, fontSize:12}}>Region</label>
                            <Select allowClear placeholder="All Regions" style={{width:'100%'}} value={aFilters.region}
                                onChange={v=>{const f={...aFilters,region:v};setAFilters(f);loadAnalytics(f);}}>
                                {[...new Set(facilities.map(f=>f.region).filter(Boolean))].sort().map(r=><Select.Option key={r} value={r}>{r}</Select.Option>)}
                            </Select>
                        </div>
                        <Button onClick={()=>loadAnalytics(aFilters)} loading={loading} type="primary" size="small">Refresh</Button>
                    </div>

                    {loading && <div style={{textAlign:'center',padding:40}}><Spin/></div>}

                    {!loading && analytics && (
                        <div>
                            {/* Score by Facility */}
                            {fc && (
                                <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                    <ExportableChart
                                        title="📍 Average Score by Facility"
                                        filename="score_by_facility"
                                        height={Math.max(240, fc.facs.length*28+80)}
                                        option={{
                                            tooltip:{trigger:'axis'},
                                            legend:{data:['Inpatient','Outpatient'], bottom:0, textStyle:{fontSize:11}},
                                            grid:{left:140, right:40, top:10, bottom:40},
                                            xAxis:{type:'value', max:100, axisLabel:{formatter:v=>`${v}%`, fontSize:10}},
                                            yAxis:{type:'category', data:fc.facs, axisLabel:{fontSize:10}},
                                            series:[
                                                {name:'Inpatient',  type:'bar', barMaxWidth:14, data:fc.ipData, itemStyle:{color:'#003580', borderRadius:[0,4,4,0]}},
                                                {name:'Outpatient', type:'bar', barMaxWidth:14, data:fc.opData, itemStyle:{color:'#006837', borderRadius:[0,4,4,0]}},
                                            ],
                                        }}/>
                                </div>
                            )}

                            {/* Score by Section */}
                            <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                <ExportableChart
                                    title="📋 Score by Section — Inpatient vs Outpatient"
                                    filename="score_by_section"
                                    height={340}
                                    option={{
                                        tooltip:{trigger:'item'},
                                        legend:{data:['Inpatient','Outpatient'], bottom:0, textStyle:{fontSize:11}},
                                        radar:{
                                            indicator: SECTIONS.map(s=>({name:s, max:100})),
                                            radius:'62%', center:['50%','48%'],
                                            axisName:{fontSize:10, fontWeight:700},
                                            splitArea:{areaStyle:{color:['#fafafa','#fff']}},
                                        },
                                        series:[{
                                            type:'radar',
                                            data:[
                                                { value:sd.ip, name:'Inpatient',  areaStyle:{color:'rgba(0,53,128,0.15)'},  lineStyle:{color:'#003580', width:2}, itemStyle:{color:'#003580'} },
                                                { value:sd.op, name:'Outpatient', areaStyle:{color:'rgba(0,104,55,0.15)'}, lineStyle:{color:'#006837', width:2}, itemStyle:{color:'#006837'} },
                                            ],
                                        }],
                                    }}/>
                            </div>

                            {/* Score by Patient Service Category */}
                            <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                <div style={{fontWeight:700,marginBottom:12,fontSize:13}}>Score by Service Category</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                    {['inpatient','outpatient'].map(type => {
                                        const rows = categoryData(type);
                                        return (
                                            <div key={type}>
                                                <div style={{fontWeight:600,fontSize:14,color:type==='inpatient'?'#003580':'#006837',marginBottom:8}}>
                                                    {type==='inpatient'?'Inpatient':'Outpatient'}
                                                </div>
                                                {rows.length===0
                                                    ? <div style={{color:'#aaa',fontSize:11}}>No data</div>
                                                    : (
                                                        <ExportableChart
                                                            filename={`score_by_category_${type}`}
                                                            height={Math.max(180, rows.length*32+60)}
                                                            option={{
                                                                tooltip:{trigger:'axis', formatter:p=>`${p[0].name}<br/>${p[0].value} response${p[0].value!==1?'s':''} - avg score ${rows[p[0].dataIndex].avg_score}%`},
                                                                grid:{left:160, right:30, top:10, bottom:10},
                                                                xAxis:{type:'value', axisLabel:{fontSize:10}},
                                                                yAxis:{type:'category', data:rows.map(r=>r.category), axisLabel:{fontSize:10}, inverse:true},
                                                                series:[{
                                                                    type:'bar', barMaxWidth:16,
                                                                    data:rows.map(r=>({ value:r.count, itemStyle:{color:scoreColor(r.avg_score)} })),
                                                                    label:{show:true, position:'right', fontSize:10, formatter:p=>`${rows[p.dataIndex].avg_score}%`},
                                                                }],
                                                            }}/>
                                                    )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Satisfaction Distribution */}
                            <div style={{background:'#fff',borderRadius:8,padding:16,marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
                                <div style={{fontWeight:700,marginBottom:12,fontSize:13}}>😊 Overall Satisfaction Distribution</div>
                                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                                    {['inpatient','outpatient'].map(type => {
                                        const data = satData(type);
                                        const total = data.reduce((a,b)=>a+b.value,0);
                                        return (
                                            <div key={type}>
                                                {total===0
                                                    ? (
                                                        <>
                                                            <div style={{fontWeight:600,fontSize:14,color:type==='inpatient'?'#003580':'#006837',marginBottom:8}}>
                                                                {type==='inpatient'?'🛏 Inpatient':'🏢 Outpatient'}
                                                            </div>
                                                            <div style={{color:'#aaa',fontSize:11}}>No data</div>
                                                        </>
                                                    )
                                                    : (
                                                        <ExportableChart
                                                            title={type==='inpatient'?'🛏 Inpatient':'🏢 Outpatient'}
                                                            filename={`satisfaction_${type}`}
                                                            height={240}
                                                            option={{
                                                                tooltip:{trigger:'item', formatter:'{b}: {c} ({d}%)'},
                                                                legend:{orient:'vertical', right:0, top:'center', textStyle:{fontSize:10}},
                                                                series:[{
                                                                    type:'pie', radius:['40%','68%'], center:['38%','50%'],
                                                                    data: data.map(d=>({ value:d.value, name:d.name, itemStyle:{color:d.color} })),
                                                                    label:{show:true, formatter:'{d}%', fontSize:10},
                                                                }],
                                                            }}/>
                                                    )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
{/* Medications Not Received */}
<div style={{background:'#fff',borderRadius:8,padding:16,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:15}}>💊 Medications Not Received</div>
        <Select allowClear placeholder="Filter by medication" style={{width:260}}
            value={medFilter} onChange={v=>setMedFilter(v)}>
            {md.map(([med])=><Select.Option key={med} value={med}>{med}</Select.Option>)}
        </Select>
    </div>
    {md.length===0
        ? <div style={{color:'#aaa',fontSize:13}}>No medication shortage data recorded yet.</div>
        : (() => {
            const shown = md.filter(([med])=>!medFilter||med===medFilter);
            return (
                <div>
                    <ExportableChart
                        filename="medications_not_received"
                        height={Math.max(200, shown.length*32+80)}
                        option={{
                            tooltip:{trigger:'axis'},
                            legend:{data:['Inpatient','Outpatient'], bottom:0, textStyle:{fontSize:11}},
                            grid:{left:180, right:40, top:10, bottom:40},
                            xAxis:{type:'value'},
                            yAxis:{type:'category', data:shown.map(([med])=>med), axisLabel:{fontSize:10}},
                            series:[
                                {name:'Inpatient',  type:'bar', stack:'total', barMaxWidth:18, data:shown.map(([,d])=>d.ip), itemStyle:{color:'#003580'}},
                                {name:'Outpatient', type:'bar', stack:'total', barMaxWidth:18, data:shown.map(([,d])=>d.op), itemStyle:{color:'#006837'}},
                            ],
                        }}/>
                    <div style={{ marginTop:12 }}>
                        {shown.map(([med,data])=>(
                            <div key={med} style={{marginBottom:8}}>
                                <div style={{ fontSize:12, fontWeight:600, color:'#d46b08', marginBottom:3 }}>{med}</div>
                                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                                    {Object.entries(data.facilities).sort((a,b)=>b[1]-a[1]).map(([fac,cnt])=>(
                                        <Tag key={fac} style={{fontSize:11}}>{fac} ({cnt})</Tag>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        })()
    }
</div>
                        </div>
                    )}

                    {!loading && !analytics && (
                        <div style={{textAlign:'center',padding:40,color:'#aaa'}}>
                            <div style={{fontSize:32,marginBottom:8}}>📊</div>
                            <div>No survey data available for the selected filters.</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── NARRATIVE REPORT (National view) ────────────────────────────────────────
// Regional and Facility views scope every section to a region or a single
// facility; the Customize panel toggles which sections render. Deliberately
// built as an inline collapsible block within the SAME Modal rather than a
// separate Drawer — a separate Drawer/Modal pairing is exactly what caused
// the click-through bug fixed earlier tonight in QoCRegionalReport.js, and
// there's no reason to reintroduce that failure mode here when a plain
// inline toggle does the same job with one fewer stacking context to fight.
const RptSectionBanner = ({ color='#1f3864', children }) => (
    <div style={{ fontSize:22, fontWeight:900, color, textAlign:'center', borderBottom:`3px solid ${color}`, paddingBottom:6, marginBottom:12 }}>{children}</div>
);
const RptKeyFindings = ({ items }) => {
    const filtered = (items||[]).filter(Boolean);
    if (!filtered.length) return null;
    return (
        <ul style={{ margin:'0 0 14px', paddingLeft:18, fontSize:14, lineHeight:1.8, color:'#333' }}>
            {filtered.map((it,i) => <li key={i} style={{ marginBottom:4 }}>{it}</li>)}
        </ul>
    );
};

const ALL_REPORT_SECTIONS = [
    { key:'summary',      label:'Executive Summary' },
    { key:'bySection',     label:'Score by Section' },
    { key:'satisfaction',  label:'Satisfaction Distribution' },
    { key:'medications',   label:'Medication Availability' },
    { key:'facilities',    label:'Facility Comparison' },
    { key:'comparison',    label:'Period Comparison' },
    { key:'complaints',    label:'Complaints & Flagged Surveys' },
    { key:'demographics',  label:'Patient Demographics' },
    { key:'categories',    label:'Service Category Breakdown' },
];

function SurveyNarrativeReport({ facilities, periods, currentPeriod, onClose }) {
    const [viewMode, setViewMode] = useState('national'); // 'national' | 'regional' | 'facility'
    const [scopeRegion, setScopeRegion]     = useState(null);
    const [scopeFacilityId, setScopeFacId]  = useState(null);
    const [filters, setFilters]   = useState({ period_label: currentPeriod?.label || '', quarters:[] });
    const [analytics, setAnalytics] = useState(null);
    const [surveys, setSurveys]   = useState([]);
    const [stats, setStats]       = useState({});
    const [loading, setLoading]   = useState(false);

    // Customize panel — which sections actually render. Defaults to everything on.
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const [enabledSections, setEnabledSections] = useState(new Set(ALL_REPORT_SECTIONS.map(s=>s.key)));
    const toggleSection = (key) => setEnabledSections(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    // Period Comparison section's second period
    const [comparisonPeriodLabel, setComparisonPeriodLabel] = useState(null);
    const [comparisonStats, setComparisonStats] = useState(null);
    const [comparisonLoading, setComparisonLoading] = useState(false);

    const SECTIONS   = ['TIMELINESS','ATTITUDE','FACILITY','SUPPLIES','PATIENT RIGHTS','OVERALL'];
    const SEC_COLORS = SECTION_COLORS;

    // Resolve the actual filter params for the current view mode/scope
    const scopedParams = (base) => {
        const p = { ...base };
        if (viewMode === 'regional' && scopeRegion) p.region = scopeRegion;
        if (viewMode === 'facility' && scopeFacilityId) p.facility_id = scopeFacilityId;
        return p;
    };

    const load = async (f = filters) => {
        setLoading(true);
        try {
            const params = scopedParams(f);
            const [aRes, sRes] = await Promise.all([
                QoCService.getSurveyAnalytics(params),
                QoCService.getCustomerSurveys(params),
            ]);
            setAnalytics(aRes.data?.data || null);
            let sData = sRes.data?.data || [];
            if (f.quarters?.length && f.period_label) {
                const selPeriod = periods.find(p => p.label === f.period_label);
                if (selPeriod) {
                    const ranges = getQuarterRanges(selPeriod);
                    sData = sData.filter(sv => {
                        const d = moment(sv.survey_date);
                        return f.quarters.some(q => { const r = ranges[q]; return r && d.isSameOrAfter(r[0]) && d.isSameOrBefore(r[1]); });
                    });
                }
            }
            setSurveys(sData);
            setStats(sRes.data?.stats || {});
        } catch { message.error('Failed to load report data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line
    // Reloading on scope change — region/facility pickers call load(filters)
    // themselves right after updating scope state, same pattern as the
    // period/quarter selectors below, so no separate effect is needed here.

    // Period Comparison — fetches the second period's stats with the SAME
    // scope (region/facility) as the main report, only when that section is
    // enabled and a comparison period has actually been picked.
    const loadComparison = async (label) => {
        if (!label) { setComparisonStats(null); return; }
        setComparisonLoading(true);
        try {
            const res = await QoCService.getCustomerSurveys(scopedParams({ period_label: label }));
            setComparisonStats(res.data?.stats || {});
        } catch { message.error('Failed to load comparison period'); }
        finally { setComparisonLoading(false); }
    };

    const sectionData = () => {
        const ip = analytics?.by_section?.inpatient  || {};
        const op = analytics?.by_section?.outpatient || {};
        return { ip: SECTIONS.map(s => ip[s] ?? 0), op: SECTIONS.map(s => op[s] ?? 0) };
    };
    const satData = (type) => {
        const SAT_LABELS = {'very_satisfied':'Very Satisfied','satisfied':'Satisfied','unsatisfied':'Unsatisfied','very_unsatisfied':'Very Unsatisfied'};
        const SAT_COLORS = {'very_satisfied':'#52c41a','satisfied':'#a0d911','unsatisfied':'#fa8c16','very_unsatisfied':'#f5222d'};
        const rows = (analytics?.satisfaction || []).filter(r => r.survey_type === type);
        return Object.keys(SAT_LABELS).map(k => ({
            name: SAT_LABELS[k], value: parseInt(rows.find(r => r.overall_satisfaction === k)?.cnt || 0), color: SAT_COLORS[k],
        })).filter(x => x.value > 0);
    };
    const medData = () => {
        const meds = analytics?.medications || [];
        const medMap = {};
        meds.forEach(r => {
            const med = r.medications.replace(/[\u2014\u2013\u2015\u00ad]/g, '-').trim();
            if (!med || med.toLowerCase() === 'other medication') return;
            if (!medMap[med]) medMap[med] = { ip:0, op:0, facilities:{} };
            if (r.survey_type === 'inpatient') medMap[med].ip += parseInt(r.cnt);
            else medMap[med].op += parseInt(r.cnt);
            if (r.facility_name) medMap[med].facilities[r.facility_name] = (medMap[med].facilities[r.facility_name]||0) + parseInt(r.cnt);
        });
        return Object.entries(medMap).sort((a,b)=>(b[1].ip+b[1].op)-(a[1].ip+a[1].op)).slice(0,10);
    };

    const sd = sectionData();
    const md = medData();
    const avg = parseFloat(stats.average_score||0);
    const satPct = stats.total_surveys > 0 ? Math.round((stats.satisfied_count/stats.total_surveys)*100) : 0;
    const weakestSectionIdx = sd.ip.map((v,i)=>Math.min(v, sd.op[i])).reduce((minI,v,i,arr)=>v<arr[minI]?i:minI, 0);
    const strongestSectionIdx = sd.ip.map((v,i)=>Math.min(v, sd.op[i])).reduce((maxI,v,i,arr)=>v>arr[maxI]?i:maxI, 0);

    const facilityRanking = () => {
        const byFac = {};
        surveys.forEach(s => {
            if (!byFac[s.facility_name]) byFac[s.facility_name] = { name:s.facility_name, region:s.region, scores:[] };
            byFac[s.facility_name].scores.push(parseFloat(s.overall_score||0));
        });
        return Object.values(byFac)
            .map(f => ({ ...f, avg: f.scores.reduce((a,b)=>a+b,0)/f.scores.length, count: f.scores.length }))
            .sort((a,b)=>b.avg-a.avg);
    };
    const ranking = facilityRanking();

    // Complaints & Flagged — anything explicitly flagged, or scoring below
    // 50%, surfaced together so a reviewer doesn't have to hunt for them.
    const flaggedSurveys = surveys.filter(s => s.is_flagged==1 || parseFloat(s.overall_score||0) < 50)
        .sort((a,b)=>parseFloat(a.overall_score||0)-parseFloat(b.overall_score||0));

    // Patient Demographics
    const demographics = () => {
        const genderCounts = {};
        const ageBuckets = { '<18':0, '18-35':0, '36-55':0, '56+':0, 'Unknown':0 };
        const paidCount = surveys.filter(s=>s.paid_for_service==='Yes').length;
        surveys.forEach(s => {
            const g = s.patient_gender || 'Unknown';
            genderCounts[g] = (genderCounts[g]||0) + 1;
            const age = parseInt(s.patient_age);
            if (!age) ageBuckets['Unknown']++;
            else if (age < 18) ageBuckets['<18']++;
            else if (age <= 35) ageBuckets['18-35']++;
            else if (age <= 55) ageBuckets['36-55']++;
            else ageBuckets['56+']++;
        });
        return { genderCounts, ageBuckets, paidCount, total: surveys.length };
    };
    const demo = demographics();

    const scopeLabel = viewMode === 'regional' ? (scopeRegion || 'Select a region')
        : viewMode === 'facility' ? (facilities.find(f=>f.id===scopeFacilityId)?.name || 'Select a facility')
        : 'National';

    const handlePrint = () => {
        const el = document.getElementById('survey-report-root');
        if (!el) { message.error('Report not found'); return; }
        const canvases = Array.from(el.querySelectorAll('canvas'));
        const restored = [];
        canvases.forEach(canvas => {
            try {
                if (canvas.width > 0 && canvas.height > 0) {
                    const img = document.createElement('img');
                    img.src = canvas.toDataURL('image/png');
                    img.style.cssText = `width:${canvas.offsetWidth}px;height:${canvas.offsetHeight}px;display:block;max-width:100%;`;
                    canvas.parentNode.replaceChild(img, canvas);
                    restored.push({ img, canvas });
                }
            } catch(e) {}
        });
        const snapshot = el.innerHTML;
        restored.forEach(({ img, canvas }) => { try { img.parentNode.replaceChild(canvas, img); } catch(e) {} });

        const styleTags = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(node => node.tagName === 'LINK' ? `<link rel="stylesheet" href="${node.href}">` : `<style>${node.textContent}</style>`)
            .join('\n');

        const win = window.open('', '_blank');
        win.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Customer Survey Report</title>' +
            styleTags +
            '<style>' +
            '*{box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}' +
            'body{margin:16px;font-family:Arial,sans-serif;font-size:12px;background:#f4f6fb;color:#222;}' +
            'img{max-width:100%!important;height:auto!important;}' +
            '.chart-box{page-break-inside:avoid;break-inside:avoid;}' +
            'tr{page-break-inside:avoid;break-inside:avoid;}' +
            'thead{display:table-header-group;}' +
            '@page{size:A4 portrait;margin:8mm;}' +
            '@media print{ *{max-height:none!important;overflow:visible!important;} }' +
            '</style></head><body>' +
            '<div style="background:#f4f6fb;">' + snapshot + '</div>' +
            '<script>window.onload=function(){setTimeout(function(){window.print();},600);};<\/script>' +
            '</body></html>');
        win.document.close();
    };

    return (
        <Modal open onCancel={onClose} footer={null} width={1040} style={{ top:10 }}
            title="Customer Survey Narrative Report" bodyStyle={{ maxHeight:'84vh', overflowY:'auto' }}>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom:12, flexWrap:'wrap' }}>
                <div style={{ minWidth:200 }}>
                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>View</label>
                    <Radio.Group value={viewMode} onChange={e=>{
                        const v = e.target.value; setViewMode(v);
                        setScopeRegion(null); setScopeFacId(null);
                        load(filters);
                    }}>
                        <Radio.Button value="national">National</Radio.Button>
                        <Radio.Button value="regional">Regional</Radio.Button>
                        <Radio.Button value="facility">Facility</Radio.Button>
                    </Radio.Group>
                </div>
                {viewMode === 'regional' && (
                    <div style={{ minWidth:180 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Region</label>
                        <Select placeholder="Select region" style={{ width:'100%' }} value={scopeRegion}
                            onChange={v=>{ setScopeRegion(v); load(filters); }}>
                            {[...new Set(facilities.map(f=>f.region).filter(Boolean))].sort().map(r=>(
                                <Select.Option key={r} value={r}>{r}</Select.Option>
                            ))}
                        </Select>
                    </div>
                )}
                {viewMode === 'facility' && (
                    <div style={{ minWidth:220 }}>
                        <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Facility</label>
                        <Select placeholder="Select facility" showSearch optionFilterProp="children" style={{ width:'100%' }} value={scopeFacilityId}
                            onChange={v=>{ setScopeFacId(v); load(filters); }}>
                            {facilities.map(f=><Select.Option key={f.id} value={f.id}>{f.name}</Select.Option>)}
                        </Select>
                    </div>
                )}
                <div style={{ minWidth:180 }}>
                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Period</label>
                    <Select allowClear placeholder="All Periods" style={{ width:'100%' }}
                        value={filters.period_label||undefined}
                        onChange={v=>{ const f={...filters,period_label:v||'',quarters:[]}; setFilters(f); load(f); }}>
                        {periods.map(p=>(<Select.Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Select.Option>))}
                    </Select>
                </div>
                <div style={{ minWidth:220 }}>
                    <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>
                        Quarter(s) <span style={{ fontWeight:400, color:'#888' }}>(select a period first)</span>
                    </label>
                    <Select mode="multiple" allowClear disabled={!filters.period_label}
                        placeholder={filters.period_label ? 'All quarters' : 'Select a period first'}
                        style={{ width:'100%' }} value={filters.quarters||[]}
                        onChange={v=>{ const f={...filters,quarters:v}; setFilters(f); load(f); }}>
                        {['Q1','Q2','Q3','Q4'].map(q => {
                            const selPeriod = periods.find(p => p.label === filters.period_label);
                            const range = selPeriod ? getQuarterRanges(selPeriod)[q] : null;
                            return <Select.Option key={q} value={q}>{q}{range && <span style={{ color:'#aaa', marginLeft:6, fontSize:11 }}>({range[0].format('DD MMM')} – {range[1].format('DD MMM YYYY')})</span>}</Select.Option>;
                        })}
                    </Select>
                </div>
                <Button icon={<span>⚙</span>} onClick={()=>setCustomizeOpen(o=>!o)}>Customize</Button>
                <Button type="primary" icon={<span>🖨</span>} onClick={handlePrint} disabled={loading}>Save as PDF</Button>
            </div>

            {customizeOpen && (
                <div style={{ background:'#f8faff', border:'1px solid #e0eaff', borderRadius:8, padding:'12px 16px', marginBottom:16 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>Report Sections</div>
                        <div style={{ display:'flex', gap:10 }}>
                            <a onClick={()=>setEnabledSections(new Set(ALL_REPORT_SECTIONS.map(s=>s.key)))}>All</a>
                            <a onClick={()=>setEnabledSections(new Set())}>None</a>
                        </div>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:14 }}>
                        {ALL_REPORT_SECTIONS.map(s => (
                            <label key={s.key} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                                <input type="checkbox" checked={enabledSections.has(s.key)} onChange={()=>toggleSection(s.key)}/>
                                {s.label}
                            </label>
                        ))}
                    </div>
                    {enabledSections.has('comparison') && (
                        <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #e0eaff' }}>
                            <label style={{ fontWeight:600, display:'block', marginBottom:4, fontSize:12 }}>Compare against period</label>
                            <Select allowClear placeholder="Select a period to compare against" style={{ width:260 }}
                                value={comparisonPeriodLabel||undefined}
                                onChange={v=>{ setComparisonPeriodLabel(v||null); loadComparison(v); }}>
                                {periods.filter(p=>p.label!==filters.period_label).map(p=>(
                                    <Select.Option key={p.label} value={p.label}>{p.label}</Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>
            )}

            {loading && <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div>}

            {!loading && (
                <div id="survey-report-root">
                    <div style={{ background:'linear-gradient(135deg,#003580,#006837)', color:'#fff', borderRadius:8, padding:'24px 28px', marginBottom:20, textAlign:'center' }}>
                        <div style={{ fontSize:11, letterSpacing:2, opacity:0.8, marginBottom:6 }}>KINGDOM OF ESWATINI · MINISTRY OF HEALTH</div>
                        <div style={{ fontSize:26, fontWeight:900 }}>Customer Satisfaction Survey Report</div>
                        <div style={{ fontSize:14, opacity:0.9, marginTop:4 }}>
                            {viewMode==='national'?'National View':viewMode==='regional'?`Regional View — ${scopeLabel}`:`Facility View — ${scopeLabel}`}
                            {' · '}{filters.period_label || 'All Periods'}{filters.quarters?.length ? ` (${filters.quarters.join(', ')})` : ''}
                        </div>
                        <div style={{ fontSize:11, opacity:0.7, marginTop:8 }}>Generated {moment().format('DD MMMM YYYY, HH:mm')}</div>
                    </div>

                    {enabledSections.has('summary') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner>EXECUTIVE SUMMARY</RptSectionBanner>
                        {(stats.total_surveys||0) === 0 ? (
                            <div style={{ color:'#aaa' }}>
                                No customer satisfaction surveys were captured
                                {viewMode!=='national' && <> for <strong>{scopeLabel}</strong></>} in the selected period.
                            </div>
                        ) : (
                        <RptKeyFindings items={[
                            <>A total of <strong>{stats.total_surveys||0}</strong> customer satisfaction surveys were captured
                                {viewMode!=='national' && <> in <strong>{scopeLabel}</strong></>}
                                ({stats.inpatient_count||0} inpatient, {stats.outpatient_count||0} outpatient).</>,
                            <>The average satisfaction score was <strong style={{color:scoreColor(avg)}}>{avg.toFixed(1)}%</strong>.</>,
                            <><strong>{satPct}%</strong> of respondents reported being satisfied or better with the service received.</>,
                            sd.ip.length>0 && (sd.ip.some(v=>v>0)||sd.op.some(v=>v>0)) && <>The <strong style={{color:SEC_COLORS[SECTIONS[strongestSectionIdx]]}}>{SECTIONS[strongestSectionIdx]}</strong> section
                                scored highest overall, while <strong style={{color:SEC_COLORS[SECTIONS[weakestSectionIdx]]}}>{SECTIONS[weakestSectionIdx]}</strong> scored
                                lowest and may need the most attention this period.</>,
                            md.length > 0 && <><strong>{md[0][0]}</strong> was the most frequently reported unavailable medication
                                ({md[0][1].ip+md[0][1].op} report{md[0][1].ip+md[0][1].op!==1?'s':''}).</>,
                            flaggedSurveys.length > 0 && <><strong>{flaggedSurveys.length}</strong> survey{flaggedSurveys.length!==1?'s':''} {flaggedSurveys.length!==1?'were':'was'} flagged or scored below 50% — see Complaints &amp; Flagged Surveys below.</>,
                        ]}/>
                        )}
                    </div>
                    )}

                    {enabledSections.has('bySection') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#8b5cf6">SCORE BY SECTION</RptSectionBanner>
                        {(!sd.ip.some(v=>v>0) && !sd.op.some(v=>v>0)) ? (
                            <div style={{ color:'#aaa' }}>No section-level score data recorded for this period.</div>
                        ) : (
                            <ExportableChart filename="report_score_by_section" height={340} option={{
                                tooltip:{trigger:'item'},
                                legend:{data:['Inpatient','Outpatient'], bottom:0, textStyle:{fontSize:11}},
                                radar:{ indicator: SECTIONS.map(s=>({name:s,max:100})), radius:'62%', axisName:{fontSize:10,fontWeight:700} },
                                series:[{ type:'radar', data:[
                                    { value:sd.ip, name:'Inpatient',  areaStyle:{color:'rgba(0,53,128,0.15)'},  lineStyle:{color:'#003580'}, itemStyle:{color:'#003580'} },
                                    { value:sd.op, name:'Outpatient', areaStyle:{color:'rgba(0,104,55,0.15)'}, lineStyle:{color:'#006837'}, itemStyle:{color:'#006837'} },
                                ]}],
                            }}/>
                        )}
                    </div>
                    )}

                    {enabledSections.has('satisfaction') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#10b981">SATISFACTION DISTRIBUTION</RptSectionBanner>
                        <Row gutter={16}>
                            {['inpatient','outpatient'].map(type => {
                                const data = satData(type); const total = data.reduce((a,b)=>a+b.value,0);
                                return (
                                    <Col span={12} key={type}>
                                        {total===0 ? <div style={{ color:'#aaa', textAlign:'center', padding:30 }}>No {type} data</div> : (
                                            <ExportableChart title={type==='inpatient'?'Inpatient':'Outpatient'} filename={`report_satisfaction_${type}`} height={240}
                                                option={{ tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'}, legend:{orient:'vertical',right:0,top:'center',textStyle:{fontSize:10}},
                                                    series:[{ type:'pie', radius:['40%','68%'], center:['38%','50%'], data:data.map(d=>({value:d.value,name:d.name,itemStyle:{color:d.color}})), label:{formatter:'{d}%',fontSize:10} }] }}/>
                                        )}
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                    )}

                    {enabledSections.has('medications') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#f59e0b">MEDICATION AVAILABILITY</RptSectionBanner>
                        {md.length===0 ? <div style={{ color:'#aaa' }}>No medication shortage data recorded for this period.</div> : (
                            <>
                                <ExportableChart filename="report_medications" height={Math.max(200, md.length*32+80)} option={{
                                    tooltip:{trigger:'axis'},
                                    legend:{data:['Inpatient','Outpatient'], bottom:0, textStyle:{fontSize:11}},
                                    grid:{left:180, right:40, top:10, bottom:40},
                                    xAxis:{type:'value'},
                                    yAxis:{type:'category', data:md.map(([m])=>m), axisLabel:{fontSize:10}},
                                    series:[
                                        {name:'Inpatient',  type:'bar', stack:'t', barMaxWidth:18, data:md.map(([,d])=>d.ip), itemStyle:{color:'#003580'}},
                                        {name:'Outpatient', type:'bar', stack:'t', barMaxWidth:18, data:md.map(([,d])=>d.op), itemStyle:{color:'#006837'}},
                                    ],
                                }}/>
                                {viewMode!=='facility' && (
                                    <div style={{ marginTop:10 }}>
                                        {md.slice(0,5).map(([medName,d])=>(
                                            Object.keys(d.facilities||{}).length>0 && (
                                                <div key={medName} style={{ marginBottom:6 }}>
                                                    <span style={{ fontSize:12, fontWeight:600, color:'#d46b08' }}>{medName}:</span>{' '}
                                                    {Object.entries(d.facilities).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([fac,cnt])=>(
                                                        <Tag key={fac} style={{ fontSize:11 }}>{fac} ({cnt})</Tag>
                                                    ))}
                                                </div>
                                            )
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    )}

                    {enabledSections.has('facilities') && viewMode!=='facility' && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#ef4444">FACILITY COMPARISON</RptSectionBanner>
                        {ranking.length===0 ? <div style={{ color:'#aaa' }}>No facility data for this period.</div> : (
                            <Table size="small" dataSource={ranking} rowKey="name" pagination={false}
                                columns={[
                                    { title:'#', width:40, render:(_,__,i)=>i+1 },
                                    { title:'Facility', dataIndex:'name' },
                                    { title:'Region', dataIndex:'region', width:120 },
                                    { title:'Surveys', dataIndex:'count', width:90, align:'center' },
                                    { title:'Avg Score', dataIndex:'avg', width:110, align:'center',
                                        render:v=><span style={{ fontWeight:800, color:scoreColor(v) }}>{v.toFixed(1)}%</span> },
                                ]}/>
                        )}
                    </div>
                    )}

                    {enabledSections.has('facilities') && viewMode==='facility' && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#ef4444">RECENT SURVEYS — {scopeLabel}</RptSectionBanner>
                        {surveys.length===0 ? <div style={{ color:'#aaa' }}>No surveys recorded for this facility in the selected period.</div> : (
                            <Table size="small" dataSource={[...surveys].sort((a,b)=>moment(b.survey_date).unix()-moment(a.survey_date).unix()).slice(0,20)}
                                rowKey="id" pagination={false}
                                columns={[
                                    { title:'Date', dataIndex:'survey_date', width:110, render:d=>moment(d).format('DD MMM YYYY') },
                                    { title:'Type', dataIndex:'survey_type', width:100, render:t=><Tag color={t==='inpatient'?'blue':'green'} style={{textTransform:'capitalize'}}>{t}</Tag> },
                                    { title:'Score', dataIndex:'overall_score', width:90, align:'center',
                                        render:v=><span style={{ fontWeight:800, color:scoreColor(parseFloat(v||0)) }}>{parseFloat(v||0).toFixed(1)}%</span> },
                                    { title:'Comments', dataIndex:'comments', ellipsis:true },
                                ]}/>
                        )}
                    </div>
                    )}

                    {enabledSections.has('comparison') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#0ea5e9">PERIOD COMPARISON</RptSectionBanner>
                        {!comparisonPeriodLabel ? (
                            <div style={{ color:'#aaa' }}>Open Customize above to pick a period to compare against.</div>
                        ) : comparisonLoading ? (
                            <div style={{ textAlign:'center', padding:30 }}><Spin/></div>
                        ) : comparisonStats ? (() => {
                            const cAvg = parseFloat(comparisonStats.average_score||0);
                            const cSatPct = comparisonStats.total_surveys > 0 ? Math.round((comparisonStats.satisfied_count/comparisonStats.total_surveys)*100) : 0;
                            const delta = avg - cAvg;
                            return (
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <div style={{ textAlign:'center', padding:'14px 0', background:'#fff', border:'1px solid #e8e8e8', borderRadius:8 }}>
                                            <div style={{ fontSize:11, color:'#888' }}>{filters.period_label||'Current'}</div>
                                            <div style={{ fontSize:26, fontWeight:900, color:scoreColor(avg) }}>{avg.toFixed(1)}%</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign:'center', padding:'14px 0', background:'#fff', border:'1px solid #e8e8e8', borderRadius:8 }}>
                                            <div style={{ fontSize:11, color:'#888' }}>{comparisonPeriodLabel}</div>
                                            <div style={{ fontSize:26, fontWeight:900, color:scoreColor(cAvg) }}>{cAvg.toFixed(1)}%</div>
                                        </div>
                                    </Col>
                                    <Col span={8}>
                                        <div style={{ textAlign:'center', padding:'14px 0', background:'#fff', border:'1px solid #e8e8e8', borderRadius:8 }}>
                                            <div style={{ fontSize:11, color:'#888' }}>Change</div>
                                            <div style={{ fontSize:26, fontWeight:900, color: delta>=0?'#52c41a':'#f5222d' }}>{delta>=0?'+':''}{delta.toFixed(1)}pt</div>
                                        </div>
                                    </Col>
                                    <Col span={24} style={{ marginTop:12 }}>
                                        <RptKeyFindings items={[
                                            <>Satisfied-or-better dropped from <strong>{Math.max(satPct,cSatPct)}%</strong> to <strong>{Math.min(satPct,cSatPct)}%</strong> — check whether this aligns with {delta<0?'the':'this'} score {delta<0?'decline':'improvement'}.</>,
                                        ]}/>
                                    </Col>
                                </Row>
                            );
                        })() : null}
                    </div>
                    )}

                    {enabledSections.has('complaints') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#f5222d">COMPLAINTS &amp; FLAGGED SURVEYS</RptSectionBanner>
                        {flaggedSurveys.length===0 ? <div style={{ color:'#aaa' }}>No flagged or low-scoring surveys ({'<'}50%) recorded for this period.</div> : (
                            <Table size="small" dataSource={flaggedSurveys} rowKey="id" pagination={false}
                                columns={[
                                    { title:'Facility', dataIndex:'facility_name', width:160 },
                                    { title:'Date', dataIndex:'survey_date', width:100, render:d=>moment(d).format('DD MMM YYYY') },
                                    { title:'Score', dataIndex:'overall_score', width:80, align:'center',
                                        render:v=><span style={{ fontWeight:800, color:scoreColor(parseFloat(v||0)) }}>{parseFloat(v||0).toFixed(1)}%</span> },
                                    { title:'Flag', dataIndex:'is_flagged', width:100, render:(v,r)=>v==1 ? <Tag color="red">{r.flag_reason||'Flagged'}</Tag> : <Tag>Low score</Tag> },
                                    { title:'Comments', dataIndex:'comments', ellipsis:true },
                                ]}/>
                        )}
                    </div>
                    )}

                    {enabledSections.has('demographics') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#6366f1">PATIENT DEMOGRAPHICS</RptSectionBanner>
                        {demo.total===0 ? <div style={{ color:'#aaa' }}>No survey data for this period.</div> : (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <ExportableChart title="By Gender" filename="report_demographics_gender" height={220} option={{
                                        tooltip:{trigger:'item',formatter:'{b}: {c} ({d}%)'},
                                        legend:{orient:'vertical', right:0, top:'center', textStyle:{fontSize:10}},
                                        series:[{ type:'pie', radius:['40%','68%'], center:['38%','50%'],
                                            data:Object.entries(demo.genderCounts).map(([k,v])=>({name:k,value:v})),
                                            label:{formatter:'{d}%',fontSize:10} }],
                                    }}/>
                                </Col>
                                <Col span={12}>
                                    <ExportableChart title="By Age Group" filename="report_demographics_age" height={220} option={{
                                        tooltip:{trigger:'axis'},
                                        grid:{left:60, right:20, top:10, bottom:30},
                                        xAxis:{type:'category', data:Object.keys(demo.ageBuckets), axisLabel:{fontSize:10}},
                                        yAxis:{type:'value'},
                                        series:[{ type:'bar', barMaxWidth:30, data:Object.values(demo.ageBuckets), itemStyle:{color:'#6366f1', borderRadius:[4,4,0,0]} }],
                                    }}/>
                                </Col>
                                <Col span={24} style={{ marginTop:8 }}>
                                    <RptKeyFindings items={[
                                        <><strong>{demo.paidCount}</strong> of <strong>{demo.total}</strong> respondents ({Math.round(demo.paidCount/demo.total*100)}%) reported paying for services.</>,
                                    ]}/>
                                </Col>
                            </Row>
                        )}
                    </div>
                    )}

                    {enabledSections.has('categories') && (
                    <div className="report-section" style={{ marginBottom:20 }}>
                        <RptSectionBanner color="#0ea5e9">SERVICE CATEGORY BREAKDOWN</RptSectionBanner>
                        <Row gutter={16}>
                            {['inpatient','outpatient'].map(type => {
                                const rows = analytics?.by_category?.[type] || [];
                                return (
                                    <Col span={12} key={type}>
                                        <div style={{ fontWeight:600, fontSize:13, marginBottom:8, color:type==='inpatient'?'#003580':'#006837' }}>
                                            {type==='inpatient'?'Inpatient':'Outpatient'}
                                        </div>
                                        {rows.length===0 ? <div style={{ color:'#aaa', fontSize:12 }}>No data</div> : (
                                            <ExportableChart filename={`report_category_${type}`} height={Math.max(180, rows.length*32+40)} option={{
                                                tooltip:{trigger:'axis', formatter:p=>`${p[0].name}<br/>${p[0].value} responses`},
                                                grid:{left:160, right:30, top:10, bottom:10},
                                                xAxis:{type:'value', axisLabel:{fontSize:10}},
                                                yAxis:{type:'category', data:rows.map(r=>r.category), axisLabel:{fontSize:10}, inverse:true},
                                                series:[{ type:'bar', barMaxWidth:16,
                                                    data:rows.map(r=>({ value:r.count, itemStyle:{color:scoreColor(r.avg_score)} })),
                                                    label:{show:true, position:'right', fontSize:10, formatter:p=>`${rows[p.dataIndex].avg_score}%`} }],
                                            }}/>
                                        )}
                                    </Col>
                                );
                            })}
                            <Col span={24} style={{ marginTop:8 }}>
                                <RptKeyFindings items={[
                                    (() => {
                                        const allRows = [...(analytics?.by_category?.inpatient||[]), ...(analytics?.by_category?.outpatient||[])];
                                        if (!allRows.length) return null;
                                        const top = allRows.reduce((a,b) => b.count > a.count ? b : a);
                                        return <><strong>{top.category}</strong> was the most common service category this period, with <strong>{top.count}</strong> responses and an average satisfaction score of <strong>{top.avg_score}%</strong>.</>;
                                    })(),
                                ]}/>
                            </Col>
                        </Row>
                    </div>
                    )}
                </div>
            )}
        </Modal>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function QoCCustomerSurveys() {
    // Get the staff member's assigned facility from localStorage
    // If client_id > 0, staff is locked to that facility
    const staffFacilityId = parseInt(window.localStorage.getItem('client_id') || '0') || null;
    const currentUserId   = parseInt(util.getUserId?.() || '0');
    const isNationalUser  = util.isNational?.() || false;
    const { periods, currentPeriod } = usePeriods();
    const [surveys, setSurveys]       = useState([]);
    const [stats, setStats]           = useState({});
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading]       = useState(false);
    const [ipModal, setIpModal]       = useState(false);
    const [opModal, setOpModal]       = useState(false);
    const [viewModal, setViewModal]   = useState(false);
    const [viewSurvey, setViewSurvey] = useState(null);
    const [filters, setFilters]       = useState({});
    const [reportOpen, setReportOpen] = useState(false);

    // ── Track selected facility's services to filter "Add" buttons ────────────
    const [filterFacServices, setFilterFacServices] = useState([]);
    const [facSearch, setFacSearch] = useState('');

    useEffect(() => {
        loadFacilities();
        trailPageView(TRAIL_MODULES.SURVEY, window.location.pathname);
    }, []);



    useEffect(() => {
        if (currentPeriod) {
            const f = { period_label: currentPeriod.label };
            setFilters(f);
            loadSurveys(f);
        }
    }, [currentPeriod?.label]);

    const loadFacilities = async () => {
        try {
            const res = await FacilityService.all({ status:1 });
            setFacilities(res.data.result.data.map(v => ({
                id: v.id,
                name: v.business_name,
                region: v.located_region || v.region || '',
                site_type: v.site_type,
                services_offered: v.services_offered || null,
            })));
        } catch {}
    };

    const loadSurveys = async (f=filters) => {
        setLoading(true);
        try {
            const res = await QoCService.getCustomerSurveys(f);
            let data = res.data?.data||[];
            // Quarter drill-down — only meaningful once a period is
            // selected, since quarters are computed relative to THAT
            // period's own start date. Multiple quarters selected = union
            // of their ranges. This has to happen client-side since it's
            // not a single date range the backend can filter on directly.
            if (f.quarters?.length && f.period_label) {
                const selPeriod = periods.find(p => p.label === f.period_label);
                if (selPeriod) {
                    const ranges = getQuarterRanges(selPeriod);
                    data = data.filter(sv => {
                        const d = moment(sv.survey_date);
                        return f.quarters.some(q => {
                            const r = ranges[q];
                            return r && d.isSameOrAfter(r[0]) && d.isSameOrBefore(r[1]);
                        });
                    });
                }
            }
            data.sort((a,b) => parseFloat(b.overall_score||0) - parseFloat(a.overall_score||0));
            setSurveys(data);
            setStats(res.data?.stats||{});
        } catch { message.error('Failed to load surveys'); }
        finally { setLoading(false); }
    };

    const handleView = async (survey) => {
        try {
            const res = await QoCService.getSurveyDetail(survey.id);
            setViewSurvey(res.data?.data||survey);
            setViewModal(true);
        } catch (e) {
            // Still show modal with basic survey data if detail fetch fails
            setViewSurvey(survey);
            setViewModal(true);
        }
    };

    const handleDelete = async (id) => {
        try {
            const survey = surveys.find(s => s.id === id);
            await QoCService.deleteSurvey(id);
            trailDelete(TRAIL_MODULES.SURVEY, (survey?.survey_type || 'Survey') + ' #' + id, id, { facilityName: survey?.facility_name });
            message.success('Deleted');
            loadSurveys();
        } catch { message.error('Failed to delete'); }
    };

    const avg = parseFloat(stats.average_score||0);
    const satPct = stats.total_surveys > 0 ? Math.round((stats.satisfied_count/stats.total_surveys)*100) : 0;

    // Determine which "Add" buttons to show based on the facility filter selection.
    // If no facility filter is active, show both. If a facility is selected,
    // show only the survey types that facility offers.
    // For staff locked to a facility, gate on that facility's own services instead
    // of the (unrelated) table filter.
    const lockedFacility = staffFacilityId ? facilities.find(f => f.id === staffFacilityId) : null;
    const lockedServices = parseServices(lockedFacility?.services_offered);
    const filterServices = parseServices(filterFacServices);
    const showIpButton   = staffFacilityId
        ? (lockedServices.length === 0 || lockedServices.includes('Inpatient'))
        : (filterServices.length === 0 || filterServices.includes('Inpatient'));
    const showOpButton   = true; // outpatient always shown

    const cols = [
        {
            title:'Facility', dataIndex:'facility_name', width:190,
            render:(n,r)=><div><div style={{fontWeight:600}}>{n}</div><div style={{fontSize:11,color:'#888'}}>{r.region}</div></div>
        },
        {
            title:'Type', dataIndex:'survey_type', width:110,
            sorter:(a,b)=>a.survey_type.localeCompare(b.survey_type),
            filters:[{text:'Inpatient',value:'inpatient'},{text:'Outpatient',value:'outpatient'}],
            onFilter:(value,record)=>record.survey_type===value,
            render:t=><Tag icon={t==='inpatient'?<UserOutlined/>:<TeamOutlined/>} color={t==='inpatient'?'blue':'green'} style={{textTransform:'capitalize'}}>{t}</Tag>
        },
        { title:'Period', dataIndex:'period_label', width:110, render:p=>p?<Tag color="blue">{p}</Tag>:<span style={{color:'#ccc'}}>—</span> },
        { title:'Date', dataIndex:'survey_date', width:150, sorter:(a,b)=>new Date(a.created_at)-new Date(b.created_at), render:(d,r)=><div><div>{moment(d).format('DD MMM YYYY')}</div><div style={{fontSize:11,color:'#888'}}>{r.created_at ? moment(r.created_at).format('HH:mm') : ''}</div></div> },
        {
            title:'Score', dataIndex:'overall_score', width:130,
            sorter:(a,b)=>parseFloat(a.overall_score)-parseFloat(b.overall_score),
            render:s=>{
                const sc=parseFloat(s||0);
                return <div>
                    <span style={{fontWeight:700,fontSize:14,color:scoreColor(sc)}}>{sc.toFixed(1)}%</span>
                    <Progress percent={sc} showInfo={false} size="small" strokeColor={scoreColor(sc)} style={{marginTop:2}}/>
                </div>;
            }
        },
        {
            title:'Gender / Age', width:110,
            render:(_,r) => (
                <div style={{fontSize:11}}>
                    {r.patient_gender && <Tag style={{fontSize:10,margin:'1px'}}>{r.patient_gender}</Tag>}
                    {r.patient_age    && <span style={{color:'#888'}}>{r.patient_age} yrs</span>}
                    {r.paid_for_service && <div style={{marginTop:2}}>
                        <Tag color={r.paid_for_service==='Yes'?'orange':'default'} style={{fontSize:9}}>
                            {r.paid_for_service==='Yes' ? `Paid E${r.amount_paid||'?'}` : 'No payment'}
                        </Tag>
                    </div>}
                </div>
            )
        },
        {
            title:'Distance', width:90,
            render:(_,r) => {
                if (!r.distance_metres && r.distance_metres !== 0) return <span style={{color:'#ccc'}}>—</span>;
                const d = parseInt(r.distance_metres);
                const color = d <= 100 ? '#52c41a' : d <= 500 ? '#fa8c16' : '#f5222d';
                return (
                    <div style={{fontSize:11}}>
                        <span style={{fontWeight:700,color}}>{d}m</span>
                        {r.is_flagged==1 && <div><Tag color="red" style={{fontSize:9,marginTop:2}}>Flagged</Tag></div>}
                    </div>
                );
            }
        },
        {
            title:'Overall', dataIndex:'overall_satisfaction', width:145,
            render:s=>{
                if(!s) return <span style={{color:'#ccc'}}>—</span>;
                const cm={very_satisfied:'success',satisfied:'processing',unsatisfied:'warning',very_unsatisfied:'error'};
                return <Tag color={cm[s]||'default'}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</Tag>;
            }
        },
        {
            title:'Action', width:100, fixed:'right',
            render:(_,rec)=>(
                <Space>
                    <Button size="small" icon={<EyeOutlined/>} onClick={()=>handleView(rec)}>View</Button>
{/* Delete disabled - surveys are permanent records */}
                </Space>
            )
        }
    ];

    const ipAccent = '#003580';
    const opAccent = '#006837';

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Customer Satisfaction Surveys</h2></div>
                <div className="my-auto ml-auto">
                    <Button icon={<span>📄</span>} onClick={()=>setReportOpen(true)}>
                        Generate Narrative Report
                    </Button>
                </div>
            </div>
            <div className="page-pad">
                {/* Staff facility lock banner */}
                {staffFacilityId ? (
                    <div style={{ background:'#e6f4ff', border:'1px solid #91d5ff',
                        borderRadius:8, padding:'8px 16px', marginBottom:12,
                        display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, fontSize:13, flexWrap:'wrap' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <span style={{ fontSize:16 }}>🔒</span>
                            <span>You are capturing surveys on behalf of patients at <strong>
                                {facilities.find(f=>f.id===staffFacilityId)?.name || 'your assigned facility'}
                            </strong>. The facility selection is locked to your assignment.</span>
                        </div>
                        <Space>
                            {showIpButton && (
                                <Button type="primary" size="small" icon={<PlusOutlined/>} onClick={()=>setIpModal(true)} style={{background:ipAccent, borderColor:ipAccent}}>
                                    Add Inpatient Survey
                                </Button>
                            )}
                            {showOpButton && (
                                <Button type="primary" size="small" icon={<PlusOutlined/>} onClick={()=>setOpModal(true)} style={{background:opAccent, borderColor:opAccent}}>
                                    Add Outpatient Survey
                                </Button>
                            )}
                        </Space>
                    </div>
                ) : (
                    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginBottom:12 }}>
                        {showIpButton && (
                            <Button type="primary" icon={<PlusOutlined/>} onClick={()=>setIpModal(true)} style={{background:ipAccent, borderColor:ipAccent}}>
                                Add Inpatient Survey
                            </Button>
                        )}
                        {showOpButton && (
                            <Button type="primary" icon={<PlusOutlined/>} onClick={()=>setOpModal(true)} style={{background:opAccent, borderColor:opAccent}}>
                                Add Outpatient Survey
                            </Button>
                        )}
                    </div>
                )}
                {/* Stats */}
                <Row gutter={16} style={{marginBottom:16}}>
                    {[
                        {title:'Total Surveys',      value:stats.total_surveys||0,  color:'#1890ff'},
                        {title:'Inpatient',          value:stats.inpatient_count||0, color:'#0050b3'},
                        {title:'Outpatient',         value:stats.outpatient_count||0,color:'#006837'},
                        {title:'Avg Score',          value:avg.toFixed(1)+'%',       color:scoreColor(avg), isStr:true},
                        {title:'Satisfied / Better', value:satPct+'%',               color:'#52c41a',       isStr:true},
                    ].map(s=>(
                        <Col span={s.isStr?6:4} key={s.title}>
                            <Card size="small" style={{borderTop:`3px solid ${s.color}`,textAlign:'center'}}>
                                <div style={{fontSize:12,color:'#888',marginBottom:4}}>{s.title}</div>
                                <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.value}</div>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <SurveyAnalyticsPanel filters={filters} periods={periods} facilities={facilities} />
                <Card>
                    <div style={{marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
                        <div style={{flex:1,minWidth:160}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Facility</label>
                            <AntdSelect allowClear placeholder="All" options={facilities} value={filters.facility_id}
                                onChange={v => {
                                    setFilters({...filters, facility_id: v});
                                    // Update services so Add buttons reflect this facility's capabilities
                                    const fac = facilities.find(f => f.id === v);
                                    setFilterFacServices(fac ? parseServices(fac.services_offered) : []);
                                }}
                                style={{width:'100%'}} />
                        </div>
                        <div style={{flex:1,minWidth:160}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Period</label>
                            <Select allowClear placeholder="All Periods" style={{width:'100%'}}
                                value={filters.period_label||undefined}
                                onChange={v=>{const f={...filters,period_label:v||'',quarters:[]};setFilters(f);loadSurveys(f);}}>
                                {periods.map(p=>(
                                    <Select.Option key={p.label} value={p.label}>
                                        {p.is_current==1?'★ ':''}{p.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{minWidth:220}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>
                                Quarter(s)
                                <span style={{fontWeight:400,color:'#888',marginLeft:6,fontSize:11}}>(select a period first)</span>
                            </label>
                            <Select
                                mode="multiple" allowClear
                                placeholder={filters.period_label ? 'All quarters' : 'Select a period first'}
                                disabled={!filters.period_label}
                                style={{width:'100%'}}
                                value={filters.quarters||[]}
                                onChange={v=>{const f={...filters,quarters:v};setFilters(f);loadSurveys(f);}}>
                                {['Q1','Q2','Q3','Q4'].map(q => {
                                    const selPeriod = periods.find(p => p.label === filters.period_label);
                                    const range = selPeriod ? getQuarterRanges(selPeriod)[q] : null;
                                    return (
                                        <Select.Option key={q} value={q}>
                                            {q}
                                            {range && (
                                                <span style={{color:'#aaa',marginLeft:6,fontSize:11}}>
                                                    ({range[0].format('DD MMM')} – {range[1].format('DD MMM YYYY')})
                                                </span>
                                            )}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </div>
                        <div style={{minWidth:150}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Region</label>
                            <Select allowClear placeholder="All Regions" style={{width:'100%'}}
                                value={filters.region||undefined}
                                onChange={v=>{const f={...filters,region:v||''};setFilters(f);loadSurveys(f);}}>
                                {[...new Set(facilities.map(f=>f.region).filter(Boolean))].sort().map(r=>(
                                    <Select.Option key={r} value={r}>{r}</Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{minWidth:120}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Type</label>
                            <Select allowClear placeholder="All" value={filters.survey_type}
                                onChange={v=>{const f={...filters,survey_type:v};setFilters(f);loadSurveys(f);}} style={{width:'100%'}}>
                                <Select.Option value="inpatient">Inpatient</Select.Option>
                                <Select.Option value="outpatient">Outpatient</Select.Option>
                            </Select>
                        </div>
                        <div style={{minWidth:210}}>
                            <label style={{fontWeight:600,display:'block',marginBottom:4}}>Date Range</label>
                            <DatePicker.RangePicker
                                onChange={v=>{const f={...filters,from_date:v?.[0]?.format('YYYY-MM-DD'),to_date:v?.[1]?.format('YYYY-MM-DD')};setFilters(f);loadSurveys(f);}}
                                style={{width:'100%'}} />
                        </div>
                    </div>
                    <Table columns={cols} dataSource={surveys} rowKey="id" loading={loading}
                        pagination={{pageSize:20,showTotal:t=>`${t} surveys`}} scroll={{x:900}} />
                </Card>
            </div>

            {/* Inpatient Modal */}
            <Modal
                title={<div style={{background:`linear-gradient(135deg,${ipAccent},#0050b3)`,margin:'-20px -24px 0',padding:'13px 24px',borderRadius:'8px 8px 0 0',color:'#fff',fontWeight:700}}>
                    <UserOutlined style={{marginRight:8}}/>Inpatient Customer Satisfaction Survey (11 Questions)
                </div>}
                open={ipModal} onCancel={()=>setIpModal(false)} footer={null}
                width={820} style={{top:20}} bodyStyle={{maxHeight:'82vh',overflowY:'auto',paddingTop:18}}>
                <SurveyForm type="inpatient" facilities={facilities}
                    periods={periods} currentPeriod={currentPeriod}
                    staffFacilityId={staffFacilityId}
                    onSaved={()=>{setIpModal(false);loadSurveys();}}
                    onClose={()=>setIpModal(false)} />
            </Modal>

            {/* Outpatient Modal */}
            <Modal
                title={<div style={{background:`linear-gradient(135deg,${opAccent},#00a651)`,margin:'-20px -24px 0',padding:'13px 24px',borderRadius:'8px 8px 0 0',color:'#fff',fontWeight:700}}>
                    <TeamOutlined style={{marginRight:8}}/>Outpatient Customer Satisfaction Survey (10 Questions)
                </div>}
                open={opModal} onCancel={()=>setOpModal(false)} footer={null}
                width={820} style={{top:20}} bodyStyle={{maxHeight:'82vh',overflowY:'auto',paddingTop:18}}>
                <SurveyForm type="outpatient" facilities={facilities}
                    periods={periods} currentPeriod={currentPeriod}
                    staffFacilityId={staffFacilityId}
                    onSaved={()=>{setOpModal(false);loadSurveys();}}
                    onClose={()=>setOpModal(false)} />
            </Modal>

            {/* View Modal */}
            <Modal
                title={viewSurvey
                    ? `${viewSurvey.survey_type === 'inpatient' ? 'Inpatient' : 'Outpatient'} Survey — ${viewSurvey.facility_name} · ${moment(viewSurvey?.survey_date).format('DD MMM YYYY')}`
                    : 'Survey'}
                open={viewModal} onCancel={()=>setViewModal(false)}
                footer={[<Button key="c" onClick={()=>setViewModal(false)}>Close</Button>]}
                width={700} bodyStyle={{maxHeight:'76vh',overflowY:'auto'}}>
                <SurveyDetail survey={viewSurvey} />
            </Modal>

            {/* Narrative Report Modal */}
            {reportOpen && (
                <SurveyNarrativeReport
                    facilities={facilities}
                    periods={periods}
                    currentPeriod={currentPeriod}
                    onClose={()=>setReportOpen(false)}
                />
            )}
        </div>
    );
}